import { useSearchParams } from 'react-router-dom';
import { Fragment, useEffect, useState, useRef, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useAuth0 } from '@auth0/auth0-react';
import './MealPlanViewer.css';
import { COLORS, FONTS } from 'styles/theme';
import { parseEstDate, getDayOfWeek, getCutoffTime, sortDeliveryDates, groupDatesByWeek, formatDeliveryDate } from 'utils/deliveryDates';
import { resolveDeliveryTime, getDeliveryTimeOptions } from 'utils/deliveryTime';
import { getMoveTargets, moveOrdersBetweenDates, isDateLocked } from 'utils/moveDelivery';
import { isResumedDate, withResumedDates, skipsOnSave, coversDate } from 'utils/skippedDates';

// Components
import CountdownLoader from 'components/mealplan/CountdownLoader';
import MealPlanHeader from 'components/mealplan/MealPlanHeader';
import { SuccessMessage, ErrorMessage, LockMessage } from 'components/mealplan/StatusMessages';
import DeliveryDateTabs from 'components/mealplan/DeliveryDateTabs';
import SkipDeliveriesModal from 'components/mealplan/SkipDeliveriesModal';
import NoMealsNoticeModal from 'components/mealplan/NoMealsNoticeModal';
import CompactMealCard from 'components/shared/CompactMealCard';
import ScrollArrows from 'components/shared/ScrollArrows';
import MealModal from 'components/shared/MealModal';
import PremiumProteinUpgradeModal from 'components/mealplan/PremiumProteinUpgradeModal';
import SummaryModal from 'components/mealplan/SummaryModal';
import MoveDeliveryModal from 'components/mealplan/MoveDeliveryModal';

// Hooks
import { useScrollArrows } from 'hooks/useScrollArrows';
import { useLockStatus } from 'hooks/useLockStatus';
import { useIsDesktop } from 'hooks/useMediaQuery';

// Order Summary Components
import { OrderSummarySidebar, FloatingOrderButton } from 'components/mealplan/order-summary';

// Services
import * as mealPlanService from 'services/mealPlanService';
import { useAuthActions, exchangeAuthToken } from 'components/auth/AuthActions';
import { getAdminKey, setAdminKey, clearAdminKey } from 'api';
import { sortQuantityFirst } from 'utils/orderSorting';
import { groupAddOnsByCategory } from 'utils/menuSections';
import { replaceSingleSelect, toggleMultiSelect } from 'utils/ingredientOptions';
import { computeAutoSubstitution } from 'utils/autoSubstitution';

// Constants
import { MEAL_TYPES, MEAL_PRICES, SUBSCRIPTION_DISCOUNT, PREMIUM_SURCHARGE, BULK_PRICING, getBulkCategory } from 'constants/meals';

// `actualIndex` the modal reports for the draft dish. A draft is deliberately
// not in `orders` yet, so it has no real index — every modal handler routes on
// this sentinel to edit the draft instead of a live order.
const DRAFT_INDEX = -1;

/** Does this delivery's subscription cover premium (upcharge) proteins? */
const isPremiumSubscription = (subscriptions, order) => {
    const mealType = order.meal || 'Unknown';
    const deliveryDate = order.deliveryDate || 'Unknown';
    return (subscriptions || []).some(subscription =>
        subscription?.mealType?.toLowerCase() === mealType?.toLowerCase()
        && subscription?.nextDeliveryDate?.toLowerCase() === deliveryDate?.toLowerCase()
        && (subscription?.tags?.includes('Premium Meat Only') || subscription?.tags?.includes('Premium Meat Only Paid')));
};

/** The customer's allergy groups this ingredient trips. Matching on the
 *  ingredient's own Allergen links keeps the payload to a few record ids rather
 *  than every ingredient they expand to, and mirrors the storefront's
 *  ingredientConflictsWithMember. */
const conflictingGroups = (ingredient, allergyGroupIds) =>
    (ingredient.allergen || []).filter(id => allergyGroupIds.includes(id));

/** Marks original ingredients active, minus allergens, substituting per
 *  computeAutoSubstitution. Takes what it needs explicitly so the initial load
 *  can run it before `customerDetails` has reached state. */
const resolveAllergyAdjustment = (order, { allergyGroupIds = [], allergyLabels = {}, isPremiumCustomer = false }) => {
    const result = computeAutoSubstitution({
        originalIngredients: order.originalIngredients || [],
        proteinOptions: order.proteinOptions,
        veggieOptions: order.veggieOptions,
        starchOptions: order.starchOptions,
        sauceOptions: order.sauceOptions,
        garnishOptions: order.garnishOptions,
        conflictsFn: (ingredient) => conflictingGroups(ingredient, allergyGroupIds).length > 0,
        conflictLabelsFn: (ingredient) => [...new Set(
            conflictingGroups(ingredient, allergyGroupIds).map(id => allergyLabels[id]).filter(Boolean)
        )],
        isPremiumCustomer,
        seed: `${order.recordId || order.dishId || order.itemName}::${order.meal}::${order.deliveryDate}`,
    });

    return {
        proteinOptions: result.proteinOptions,
        veggieOptions: result.veggieOptions,
        starchOptions: result.starchOptions,
        sauceOptions: result.sauceOptions,
        garnishOptions: result.garnishOptions,
        finalIngredients: result.finalIngredients,
        autoSubstitutions: result.substitutions,
        allergyAdjusted: result.wasAdjusted,
        hasAllergenConflict: result.hasAllergenConflict,
        allergenConflicts: result.allergenConflicts,
        selectionResolved: true,
    };
};

/** Is there anything queued that a save would actually send? Mirrors the test the
 *  summary's own Save button uses: optimizePendingChanges can collapse changes
 *  back to an empty array (add a dish, then remove it), which leaves isDirty true
 *  with nothing to send — so the length matters, not just the flag. */
const hasUnsavedIn = (pendingChanges) => Object.values(pendingChanges || {})
    .some(entry => entry?.isDirty && entry?.changes?.length > 0);

const newChangeId = () => `change-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

const isMoveInto = (date) => (change) => change.type === 'MOVE_DELIVERY_DATE' && change.toDate === date;

/** A pending move must land on whatever slot its target date ends up with. */
const retimeMovesInto = (pendingChanges, toDate, deliveryTime) => Object.fromEntries(
    Object.entries(pendingChanges).map(([date, entry]) => [date, {
        ...entry,
        changes: entry.changes.map(c => (isMoveInto(toDate)(c) ? { ...c, deliveryTime } : c))
    }])
);

/** A dish on the menu for this date that isn't in the plan yet. */
const isAvailableDish = (order) => Boolean(order) && order.isCore && !order.isOrdered;

/** Adjusts every dish the customer could still add, so the cards show what they'd
 *  be served. Dishes already in the plan are left exactly as saved. */
const adjustAvailableDishes = (orders, customerDetails) => {
    const adjusted = (orders || []).map((order) => {
        if (!isAvailableDish(order)) return order;
        return {
            ...order,
            ...resolveAllergyAdjustment(order, {
                allergyGroupIds: customerDetails?.customer?.clientAllergyGroups?.[order.meal] || [],
                allergyLabels: customerDetails?.customer?.clientAllergyLabels || {},
                isPremiumCustomer: isPremiumSubscription(customerDetails?.subscriptions, order),
            }),
        };
    });
    return sortQuantityFirst(adjusted, undefined, (order) => Boolean(order.hasAllergenConflict));
};

function GateCard({ title, body, action, onAction, disabled, children }) {
    return (
        <div style={{ maxWidth: '480px', margin: '0 auto', padding: '60px 16px', textAlign: 'center', fontFamily: FONTS.sans }}>
            <h2 style={{ fontFamily: FONTS.serif, color: COLORS.dark, marginBottom: '10px' }}>{title}</h2>
            <p style={{ color: COLORS.text, fontSize: '15px', lineHeight: 1.5 }}>{body}</p>
            {children}
            <button
                type="button"
                onClick={onAction}
                disabled={disabled}
                style={{
                    marginTop: '16px', background: COLORS.orange, border: 'none', borderRadius: '8px',
                    padding: '11px 26px', cursor: disabled ? 'default' : 'pointer', color: '#fff',
                    fontWeight: 700, fontSize: '15px', opacity: disabled ? 0.6 : 1,
                }}
            >
                {action}
            </button>
        </div>
    );
}

export default function MealPlanViewer() {
    const [params, setParams] = useSearchParams();
    const token = params.get('customer');
    const { isAuthenticated, isLoading: authLoading, getAccessTokenSilently } = useAuth0();
    const { handleLogin, busy: loginBusy, openProfilePicker } = useAuthActions();
    const [notOwner, setNotOwner] = useState(false);
    // ?admin=1 lets staff open any customer's plan with the shared admin password instead of Auth0.
    const adminRequested = params.get('admin') === '1';
    const [adminKey, setAdminKeyState] = useState(getAdminKey);
    const [adminInput, setAdminInput] = useState('');
    const [adminError, setAdminError] = useState('');
    const showAdminPrompt = !adminKey && (adminRequested || !!adminError);

    // State
    const [customerDetails, setCustomerDetails] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    // eslint-disable-next-line no-unused-vars
    const [saving, setSaving] = useState(false); // May be used for future batch save loading state
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState('');
    const [resumeError, setResumeError] = useState('');
    const [resumingDate, setResumingDate] = useState(null);
    const [noMealsNotice, setNoMealsNotice] = useState([]);
    const noMealsBaselineRef = useRef(null);
    const noMealsNoticeShownRef = useRef(new Set());

    // Modal state
    const [selectedMeal, setSelectedMeal] = useState(null);
    // A dish being configured but NOT yet in the plan. The card's Customize
    // button builds this, the modal edits it, and only "Add to Cart" commits it
    // (Claire, Aug 2026) — nothing reaches `orders`, the billing summary or
    // `pendingChanges` before that click.
    const [draftMeal, setDraftMeal] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [showPriceConfirm, setShowPriceConfirm] = useState(null);
    const [showSummaryModal, setShowSummaryModal] = useState(false);
    // Desktop order summary is a cart-style drawer now: closed by default,
    // opened via the floating cart button (was a permanently-fixed sidebar
    // that covered the site header and couldn't be dismissed).
    const [summaryOpen, setSummaryOpen] = useState(false);

    // Price change state
    const [billingChanges, setBillingChanges] = useState(null);
    const [priorPaymentsByDate, setPriorPaymentsByDate] = useState({});
    const [paymentRecords, setPaymentRecords] = useState({}); // { [date]: [{ amount, stripeSessionId, timestamp }] }

    // Delivery date state
    const [deliveryDates, setDeliveryDates] = useState([]);
    const [selectedDeliveryDate, setSelectedDeliveryDate] = useState(null);
    const [ordersByDeliveryDate, setOrdersByDeliveryDate] = useState({});
    const [deliveryStatusState, setDeliveryStatusState] = useState({});

    // Pending changes state - tracks unsaved changes per delivery date
    const [pendingChanges, setPendingChanges] = useState({});
    // Delivery time overrides per date - { [date]: 'Evening' }
    const [deliveryTimeOverrides, setDeliveryTimeOverrides] = useState({});
    const [showMoveModal, setShowMoveModal] = useState(false);
    // A fresh object per move, so moving onto the same date twice still reopens its tab
    const [reopenTabRequest, setReopenTabRequest] = useState(null);
    const [showSkipModal, setShowSkipModal] = useState(false);
    // Structure:
    // {
    //   "2025-01-15": {
    //     isDirty: true,
    //     changes: [{ type: 'ADD_DISH', ... }, { type: 'REPLACE_PROTEIN', ... }, ...],
    //     originalOrders: [...] // snapshot for discard/rollback
    //   }
    // }

    // Refs
    const modalRef = useRef(null);
    const containerRef = useRef(null);

    // Custom hooks
    const { shouldShowArrows, getScrollState, scrollLeft, scrollRight, handleScroll, initializeScrollPositions } = useScrollArrows();
    const isLocked = useLockStatus(selectedDeliveryDate);
    const panelBorder = isLocked ? '#efb3b0' : '#f6cf9f';
    const isDesktop = useIsDesktop();

    // Payment processing state — shown as overlay modal while waiting for webhook
    const [paymentProcessingModal, setPaymentProcessingModal] = useState(null); // null | 'processing' | 'success' | 'failed' | 'timeout'
    const paymentProcessedRef = useRef(false);

    const hasUnsavedChanges = hasUnsavedIn(pendingChanges);

    // Every new unsaved change pops the desktop summary open — not just the first,
    // so the customer always lands on what changed and where Save is. Keyed on
    // `pendingChanges` rather than the boolean: across successive edits the flag
    // stays true, and only the object identity tells us a new change landed.
    // Nothing here ever closes it — the × and the backdrop are the only way out.
    useEffect(() => {
        // Desktop only: on mobile the summary is a full-screen modal, and throwing
        // that over the menu mid-edit would hijack the page.
        if (isDesktop && hasUnsavedIn(pendingChanges)) setSummaryOpen(true);
    }, [pendingChanges, isDesktop]);

    // Effects — skip initial fetch if this is a payment redirect (payment handler will fetch once)
    useEffect(() => {
        if (adminKey) {
            if (!token) {
                setError('No customer token provided');
                setLoading(false);
                return;
            }
        } else if (showAdminPrompt) {
            return;
        // Fetching before Auth0 settles would go out without a bearer token and 401.
        } else if (authLoading || !isAuthenticated) {
            return;
        } else if (!token) {
            (async () => {
                const result = await exchangeAuthToken(await getAccessTokenSilently({
                    authorizationParams: {
                        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                        scope: 'openid profile email offline_access',
                    },
                })).catch(() => ({ ok: false }));
                if (result.ok) {
                    setParams({ customer: result.token }, { replace: true });
                } else if (result.needsProfile) {
                    openProfilePicker();
                } else if (result.noAccount) {
                    window.location.replace('/menu');
                } else {
                    setError(result.error || "Couldn't load your account. Please try again.");
                    setLoading(false);
                }
            })();
            return;
        }

        const isPaymentRedirect = params.get('payment') === 'success' && params.get('pendingId');
        const isCancelRedirect = params.get('payment') === 'cancelled';

        if (isPaymentRedirect) {
            // Payment redirect — don't fetch yet, let the handler below do it after polling
            handlePaymentSuccess(params.get('pendingId'));
        } else if (isCancelRedirect) {
            // Cancelled — fetch normally but show message
            paymentProcessedRef.current = true;
            fetchMealPlan();
            setError('Payment was cancelled. Your unsaved changes have been discarded.');
            window.history.replaceState({}, '', `/meal-plan?customer=${token}`);
            setTimeout(() => setError(''), 10000);
        } else {
            // Normal page load
            fetchMealPlan();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, authLoading, isAuthenticated, adminKey, showAdminPrompt]);

    // Handle successful payment redirect — poll for webhook, then single fetch
    const handlePaymentSuccess = async (pendingId) => {
        if (paymentProcessedRef.current) return;
        paymentProcessedRef.current = true;
        setPaymentProcessingModal('processing');

        // Poll until webhook has processed the pending changes
        let finalStatus = 'timeout';
        const maxAttempts = 30;
        for (let i = 0; i < maxAttempts; i++) {
            try {
                const result = await mealPlanService.checkPendingStatus(pendingId);
                if (result.status === 'completed') { finalStatus = 'completed'; break; }
                if (result.status === 'save_failed') { finalStatus = 'save_failed'; break; }
            } catch (e) { /* ignore polling errors */ }
            await new Promise(resolve => setTimeout(resolve, 1000));
        }

        window.history.replaceState({}, '', `/meal-plan?customer=${token}`);

        // Single fetch — loads the final state
        await fetchMealPlan();

        if (finalStatus === 'completed') {
            setPaymentProcessingModal('success');
        } else if (finalStatus === 'save_failed') {
            setPaymentProcessingModal('failed');
        } else {
            setPaymentProcessingModal('timeout');
        }
    };

    useEffect(() => {
        const timeout = setTimeout(() => {
            initializeScrollPositions(MEAL_TYPES);
        }, 100);
        return () => clearTimeout(timeout);
    }, [orders, initializeScrollPositions]);

    useEffect(() => {
        if (customerDetails?.customer?.name) {
            document.title = `Square Fare for ${customerDetails.customer.name}`;
        } else if (!loading) {
            document.title = 'Square Fare Meal Plan';
        }
    }, [customerDetails?.customer?.name, loading]);

    // Update delivery statuses when customerDetails or deliveryDates change
    useEffect(() => {
        updateDeliveryStatuses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerDetails?.subscriptions, ordersByDeliveryDate]);

    // Week-based billing calculation with shared subscription pool
    // Key principle: Weekly subscription is a shared pool across all dates in the week
    // Moving meals within a week = $0 as long as weekly total matches
    // Premium proteins are tracked weekly (counts of premium dinners, premium lunches, etc.)
    const calculateBillingChanges = useCallback(() => {
        if (!customerDetails?.subscriptions || !deliveryDates.length || !ordersByDeliveryDate) return null;
        const subs = withResumedDates(customerDetails.subscriptions, ordersByDeliveryDate);

        let totalCharges = 0;
        let totalRefunds = 0;
        let totalDeliveryFee = 0;
        const dates = {};
        const datesWithChanges = [];
        //debug
        //console.log('customerDetails', customerDetails);
        //console.log('ordersByDeliveryDate', ordersByDeliveryDate);

        // Group delivery dates by week (keyed by its Sunday), e.g. 2026-02-15: ['2026-02-15', '2026-02-17', '2026-02-19']
        const datesByWeek = groupDatesByWeek(deliveryDates);
        //console.log('deliveryDates', deliveryDates);

        // Track premium usage per week per meal type
        const premiumUsageByWeek = {}; // { weekKey: { mealType: { subscribed, ordered, hasExceeded } } }

        Object.entries(datesByWeek).forEach(([weekKey, datesInWeek]) => {
            // ===== PASS 1: Build weekly billing structure matching backend logic =====
            // Structure: { mealType: { subscribed: number, ordered: number, premium: { proteinName: quantity }, price: [] } }
            const weeklyMealsByType = {};
            const snacksPerDay = customerDetails.customer?.snacksPerDay || 1;

            // Initialize meal types
            MEAL_TYPES.forEach(mealType => {
                if (mealType !== 'Add On') {
                    weeklyMealsByType[mealType] = {
                        subscribed: 0,
                        ordered: 0,
                        premium: {},
                        price: [],
                        premiumSubscriptions: 0 // Track count of premium subscriptions
                    };
                }
            });
            weeklyMealsByType['Add On'] = {}; // Track by dishId: { dishId: quantity }
            const prepaidAddOns = {}; // { dishId: { remaining, price, itemName, date } }

            // First, collect all subscriptions for the week
            datesInWeek.forEach(date => {
                const matchingSubscriptions = subs.filter(sub => coversDate(sub, date));
                matchingSubscriptions.forEach(sub => {
                    if (sub.isPreSetAddOn && sub.preSetDishId != null) {
                        const key = String(sub.preSetDishId);
                        if (!prepaidAddOns[key]) {
                            const itemName = Object.values(ordersByDeliveryDate).flat()
                                .find(o => String(o.dishId) === key)?.itemName || 'Pre-set add-on';
                            prepaidAddOns[key] = { remaining: 0, price: sub.preSetPrice || 0, itemName, date };
                        }
                        prepaidAddOns[key].remaining += sub.mealsIncluded || 0;
                        return;
                    }

                    // Normalize to canonical MEAL_TYPES casing (e.g. "Add on" -> "Add On")
                    const mealType = MEAL_TYPES.find(t => t.toLowerCase() === (sub.mealType || '').toLowerCase()) || sub.mealType;
                    if (mealType === 'Add On') return;

                    const mealsIncluded = sub.mealsIncluded || 0;
                    if (!weeklyMealsByType[mealType]) {
                        weeklyMealsByType[mealType] = {
                            subscribed: 0,
                            ordered: 0,
                            premium: {},
                            price: [],
                            premiumSubscriptions: 0
                        };
                    }
                    weeklyMealsByType[mealType].subscribed += mealsIncluded;

                    // Count premium subscriptions (Premium Meat Only Paid)
                    if (sub.tags?.includes('Premium Meat Only Paid')) {
                        weeklyMealsByType[mealType].premiumSubscriptions += mealsIncluded;
                    }
                });
            });

            // Adjust snack pool by snacksPerDay
            // if (weeklyMealsByType['Snack'] && customerDetails.customer?.snacksPerDay) {
            //     weeklyMealsByType['Snack'].regular = weeklyMealsByType['Snack'].regular * customerDetails.customer.snacksPerDay;
            // }

            // Track orders by date for charge allocation and per-order details
            const ordersByDateAndType = {}; // { date: { mealType: { ordered: number, premium: { proteinName: quantity }, price: [], premiumCharge: { proteinName: charge } } } }
            const orderDetailsByDate = {}; // { date: { mealType: [ { recordId, itemName, quantity, price, isIncluded, premiumProtein: { name, charge } } ] } } }
            datesInWeek.forEach(date => {
                ordersByDateAndType[date] = {};
                orderDetailsByDate[date] = {};
                MEAL_TYPES.forEach(mealType => {
                    if (mealType !== 'Add On') {
                        ordersByDateAndType[date][mealType] = {
                            ordered: 0,
                            premium: {},
                            price: [],
                            premiumCharge: {} // Track charge per protein name
                        };
                        orderDetailsByDate[date][mealType] = [];
                    }
                });
                ordersByDateAndType[date]['Add On'] = {}; // Track by dishId
                orderDetailsByDate[date]['Add On'] = [];
            });

            // Then, collect all ordered meals for each date
            datesInWeek.forEach(date => {
                const ordersForDate = ordersByDeliveryDate[date] || [];

                ordersForDate.forEach(order => {
                    // Normalize meal type to canonical MEAL_TYPES casing (e.g. "Add on" -> "Add On")
                    const rawMeal = order.meal || 'Unknown';
                    const mealType = MEAL_TYPES.find(t => t.toLowerCase() === rawMeal.toLowerCase()) || rawMeal;
                    const quantity = order.quantity || 0;
                    const dishId = order.dishId;

                    if (mealType === 'Add On' && quantity > 0) {
                        // Track add-ons by dishId
                        if (!weeklyMealsByType['Add On'][dishId]) {
                            weeklyMealsByType['Add On'][dishId] = 0;
                        }
                        weeklyMealsByType['Add On'][dishId] += quantity;

                        if (!ordersByDateAndType[date]['Add On'][dishId]) {
                            ordersByDateAndType[date]['Add On'][dishId] = 0;
                        }
                        ordersByDateAndType[date]['Add On'][dishId] += quantity;

                        // Store order details for add-ons
                        const price = order.price || 0;
                        orderDetailsByDate[date]['Add On'].push({
                            recordId: order.recordId,
                            dishId: dishId,
                            itemName: order.itemName,
                            quantity: quantity,
                            price: price, // Add-ons are never included
                            isIncluded: false,
                            premiumProtein: null // Add-ons don't have premium proteins
                        });
                        return;
                    }

                    if (order.isOrdered && quantity > 0 && mealType !== 'Add On') {
                        const price = order.price || MEAL_PRICES[mealType] || 0;
                        const activeProtein = order.proteinOptions?.find(opt => opt.isActive);
                        const premiumProteinPrice = activeProtein?.priceSubscription || 0;
                        const proteinName = activeProtein?.name || activeProtein?.variantName || '';

                        // Track ordered meals
                        weeklyMealsByType[mealType].ordered += quantity;
                        ordersByDateAndType[date][mealType].ordered += quantity;

                        // Track prices (for non-lunch/dinner meal types)
                        for (let i = 0; i < quantity; i++) {
                            weeklyMealsByType[mealType].price.push(price);
                            ordersByDateAndType[date][mealType].price.push(price);
                        }

                        // Track ALL premium proteins by name (we'll calculate charges later based on subscription count)
                        let premiumProteinInfo = null;
                        if (premiumProteinPrice > 0 && proteinName) {
                            if (!weeklyMealsByType[mealType].premium[proteinName]) {
                                weeklyMealsByType[mealType].premium[proteinName] = 0;
                            }
                            weeklyMealsByType[mealType].premium[proteinName] += quantity;

                            if (!ordersByDateAndType[date][mealType].premium[proteinName]) {
                                ordersByDateAndType[date][mealType].premium[proteinName] = 0;
                            }
                            ordersByDateAndType[date][mealType].premium[proteinName] += quantity;

                            // Store the potential premium protein charge (will be adjusted later based on subscription coverage)
                            if (!ordersByDateAndType[date][mealType].premiumCharge[proteinName]) {
                                ordersByDateAndType[date][mealType].premiumCharge[proteinName] = 0;
                            }
                            ordersByDateAndType[date][mealType].premiumCharge[proteinName] += premiumProteinPrice * quantity;

                            // Temporarily track full charge - will be adjusted in PASS 3
                            premiumProteinInfo = {
                                name: proteinName,
                                charge: premiumProteinPrice * quantity
                            };
                        }

                        // Store order details (will be updated in PASS 3 after pool allocation)
                        orderDetailsByDate[date][mealType].push({
                            recordId: order.recordId,
                            itemName: order.itemName,
                            quantity: quantity,
                            price: price, // Will be updated to 0 if covered
                            isIncluded: false, // Will be updated in PASS 3
                            premiumProtein: premiumProteinInfo
                        });
                    }
                });
            });

            // ===== PASS 1.5: Allocate the weekly premium-protein pool =====
            // The pool (weeklyPremiumSubscriptions) covers the customer's highest-priced premium
            // picks for the week first; anything beyond the pool is charged, cheapest-first. This
            // runs once per meal type across the whole week (not per date), so every date's charge
            // is read from the same global ranking instead of a per-date proportional estimate.
            //
            // Locked (past-deadline) dates get first claim on the pool, settled among themselves
            // only — so their charge is fixed the moment they lock and can never shift because of
            // an edit made later on a different, still-unlocked date. Whatever pool is left over
            // after locked dates are settled goes to unlocked dates.
            const lockedDatesInWeek = datesInWeek.filter(date => {
                const lockDate = getCutoffTime(date);
                return lockDate && new Date() > lockDate;
            });
            const unlockedDatesInWeek = datesInWeek.filter(date => !lockedDatesInWeek.includes(date));

            const weeklyPremiumChargeByOrder = {}; // { [mealType]: { "date::recordId": chargeAmount } }
            MEAL_TYPES.forEach(mealType => {
                if (mealType === 'Add On') return;
                weeklyPremiumChargeByOrder[mealType] = {};

                const buildEntries = (dates) => {
                    const entries = [];
                    dates.forEach(date => {
                        (orderDetailsByDate[date][mealType] || []).forEach(od => {
                            if (od.premiumProtein) {
                                entries.push({
                                    key: `${date}::${od.recordId}`,
                                    quantity: od.quantity,
                                    pricePerUnit: od.premiumProtein.charge / od.quantity
                                });
                            }
                        });
                    });
                    // Highest-priced picks are covered by the pool first; the rest is charged cheapest-first
                    entries.sort((a, b) => b.pricePerUnit - a.pricePerUnit);
                    return entries;
                };

                let poolRemaining = weeklyMealsByType[mealType].premiumSubscriptions || 0;

                // Locked dates claim the pool first, in isolation from unlocked dates
                buildEntries(lockedDatesInWeek).forEach(entry => {
                    const covered = Math.min(entry.quantity, poolRemaining);
                    const charged = entry.quantity - covered;
                    poolRemaining -= covered;
                    weeklyPremiumChargeByOrder[mealType][entry.key] = charged * entry.pricePerUnit;
                });

                // Unlocked dates share whatever pool remains
                buildEntries(unlockedDatesInWeek).forEach(entry => {
                    const covered = Math.min(entry.quantity, poolRemaining);
                    const charged = entry.quantity - covered;
                    poolRemaining -= covered;
                    weeklyPremiumChargeByOrder[mealType][entry.key] = charged * entry.pricePerUnit;
                });
            });

            // ===== PASS 2: Allocate subscription pool to dates (prioritize default delivery dates) =====
            // Sort dates: default delivery days first, then others
            const sortedDates = [...datesInWeek].sort((a, b) => {
                const aHasSubscription = subs.some(sub => coversDate(sub, a));
                const bHasSubscription = subs.some(sub => coversDate(sub, b));
                if (aHasSubscription && !bHasSubscription) return -1;
                if (!aHasSubscription && bHasSubscription) return 1;
                return 0;
            });

            const defaultDate = sortedDates.find(date => subs.some(sub => coversDate(sub, date))) || datesInWeek[0];

            // Add-on pre-set plans prepay that specific dish; unused units are refunded at its price, less the subscription discount
            sortedDates.forEach(date => {
                orderDetailsByDate[date]['Add On'] = orderDetailsByDate[date]['Add On'].flatMap(od => {
                    const prepaid = prepaidAddOns[String(od.dishId)];
                    const covered = prepaid ? Math.min(od.quantity, prepaid.remaining) : 0;
                    if (!covered) return [od];
                    prepaid.remaining -= covered;
                    const coveredEntry = { ...od, quantity: covered, isIncluded: true, price: 0 };
                    return covered === od.quantity ? [coveredEntry] : [coveredEntry, { ...od, quantity: od.quantity - covered }];
                });
            });
            const prepaidRefundsByDate = {}; // { date: [{ dishId, itemName, quantity, amount }] }
            Object.entries(prepaidAddOns).forEach(([dishId, prepaid]) => {
                if (prepaid.remaining <= 0) return;
                if (!prepaidRefundsByDate[prepaid.date]) prepaidRefundsByDate[prepaid.date] = [];
                prepaidRefundsByDate[prepaid.date].push({
                    dishId,
                    itemName: prepaid.itemName,
                    quantity: prepaid.remaining,
                    amount: prepaid.remaining * prepaid.price * SUBSCRIPTION_DISCOUNT
                });
            });

            // Track remaining pool as we allocate
            const remainingPoolByType = {};
            MEAL_TYPES.forEach(mealType => {
                if (mealType !== 'Add On') {
                    const mealData = weeklyMealsByType[mealType];
                    remainingPoolByType[mealType] = mealData.subscribed || 0;
                }
            });

            // Track how many meals from each date are covered by pool
            const coveredByPool = {}; // { date: { mealType: number } }
            datesInWeek.forEach(date => {
                coveredByPool[date] = {};
                MEAL_TYPES.forEach(mealType => {
                    if (mealType !== 'Add On') {
                        coveredByPool[date][mealType] = 0;
                    }
                });
            });

            const shareOn = (date, mealType) => subs
                .filter(sub => coversDate(sub, date) && !(sub.isPreSetAddOn && sub.preSetDishId != null)
                    && (sub.mealType || '').toLowerCase() === mealType.toLowerCase())
                .reduce((sum, sub) => sum + (sub.mealsIncluded || 0), 0);

            // Pool covers the week's priciest units so the cheapest are charged, matching billing script; among equal
            // prices a date's own plan meals go first, so an over-plan meal is never one its own plan should cover
            MEAL_TYPES.forEach(mealType => {
                if (mealType === 'Add On') return;
                const isFixedPrice = mealType === 'Lunch' || mealType === 'Dinner';
                const units = sortedDates.flatMap(date => {
                    const share = shareOn(date, mealType);
                    return [...(ordersByDateAndType[date][mealType].price || [])]
                        .sort((a, b) => b - a)
                        .map((price, i) => ({ date, price: isFixedPrice ? 0 : price, own: i < share }));
                });
                units.sort((a, b) => b.price - a.price || Number(b.own) - Number(a.own));
                units.slice(0, remainingPoolByType[mealType] || 0).forEach(({ date }) => {
                    coveredByPool[date][mealType] += 1;
                    remainingPoolByType[mealType] -= 1;
                });
            });

            // Each date's own plan share covers its meals first and refunds whatever it doesn't use; pool meals beyond a
            // date's own share are charged on that date at the plan rate, and meals beyond the pool at the normal price.
            const planRate = (mealType) => SUBSCRIPTION_DISCOUNT * ((MEAL_PRICES[mealType] || 0)
                + (mealType !== 'Snack' && (weeklyMealsByType[mealType]?.premiumSubscriptions || 0) > 0 ? PREMIUM_SURCHARGE : 0));
            const poolSplit = Object.fromEntries(datesInWeek.map(date => [date, {}])); // { date: { mealType: { own, borrowed, refund } } }
            MEAL_TYPES.forEach(mealType => {
                if (mealType === 'Add On' || mealType === 'Snack') return;
                datesInWeek.forEach(date => {
                    const share = shareOn(date, mealType);
                    const covered = coveredByPool[date][mealType];
                    const own = Math.min(covered, share);
                    poolSplit[date][mealType] = { own, borrowed: covered - own, refund: share - own };
                });
            });

            // Snack pool is subscribed / snacksPerDay (billing script), so only the unused part is refunded, skipped dates first.
            let snacksLeft = Math.max(0, weeklyMealsByType.Snack.subscribed / snacksPerDay - weeklyMealsByType.Snack.ordered);
            [...sortedDates.filter(date => deliveryStatusState[date]?.isSkipped), ...sortedDates.filter(date => !deliveryStatusState[date]?.isSkipped)]
                .forEach(date => {
                    const refund = Math.min(snacksLeft, Math.max(0, shareOn(date, 'Snack') / snacksPerDay - ordersByDateAndType[date].Snack.ordered));
                    snacksLeft -= refund;
                    poolSplit[date].Snack = { own: coveredByPool[date].Snack, borrowed: 0, refund };
                });
            if (snacksLeft > 1e-9) poolSplit[defaultDate].Snack.refund += snacksLeft;

            // ===== PASS 3: Calculate charges per date following backend logic =====
            datesInWeek.forEach(date => {
                const lockDate = getCutoffTime(date);
                const isLocked = lockDate && new Date() > lockDate;
                const localSkipStatus = deliveryStatusState[date]?.isSkipped;
                const matchingSubscriptions = subs.filter(sub => coversDate(sub, date));
                const isDateSkipped = localSkipStatus !== undefined
                    ? localSkipStatus
                    : matchingSubscriptions.some(sub => sub.isSkipped);

                // Collect orders for this date
                const ordersForDate = ordersByDeliveryDate[date] || [];
                const dateOrderedByType = {};
                const dateSubscribedByType = {};

                ordersForDate.forEach(order => {
                    const mealType = order.meal || 'Unknown';
                    if (mealType !== 'Add On') {
                        dateOrderedByType[mealType] = (dateOrderedByType[mealType] || 0) + (order.quantity || 0);
                    }
                });

                matchingSubscriptions.forEach(sub => {
                    if (sub.isPreSetAddOn && sub.preSetDishId != null) return;
                    const mealType = sub.mealType;
                    dateSubscribedByType[mealType] = (dateSubscribedByType[mealType] || 0) + (sub.mealsIncluded || 0);
                });

                // Build meals structure for this date
                const dateMeals = {};
                let dateCharges = 0;
                let dateRefunds = 0;

                // Process each meal type following backend logic
                MEAL_TYPES.forEach(mealType => {
                    if (mealType === 'Add On') {
                        // Add-ons: apply bulk pricing based on weekly totals per category
                        const addOnOrderDetails = orderDetailsByDate[date]['Add On'] || [];
                        let addOnOrdered = 0;
                        let addOnAmount = 0;

                        // Weekly totals per bulk category, counting only units not covered by a pre-set plan
                        const weeklyBulkTotals = {};
                        datesInWeek.forEach(d => {
                            (orderDetailsByDate[d]?.['Add On'] || []).forEach(od => {
                                if (od.isIncluded) return;
                                const cat = getBulkCategory(od.itemName);
                                if (cat) weeklyBulkTotals[cat] = (weeklyBulkTotals[cat] || 0) + od.quantity;
                            });
                        });

                        addOnOrderDetails.forEach(orderDetail => {
                            addOnOrdered += orderDetail.quantity;
                            if (orderDetail.isIncluded) return;
                            const cat = getBulkCategory(orderDetail.itemName);
                            let unitPrice = orderDetail.price;

                            if (cat && BULK_PRICING[cat] && weeklyBulkTotals[cat] >= BULK_PRICING[cat].bulkMin) {
                                unitPrice = BULK_PRICING[cat].bulkEach;
                            }

                            const itemTotal = unitPrice * orderDetail.quantity;
                            addOnAmount += itemTotal;
                            // Update the order detail price for display
                            orderDetail.price = unitPrice;
                        });

                        if (addOnOrdered > 0) {
                            dateMeals['Add On'] = {
                                subscribed: 0,
                                ordered: addOnOrdered,
                                delta: addOnOrdered,
                                amount: addOnAmount,
                                premiumProteins: { count: 0, charge: 0 },
                                orderDetails: addOnOrderDetails
                            };
                            dateCharges += addOnAmount;
                        }
                        return;
                    }

                    const dateOrders = ordersByDateAndType[date][mealType];
                    const ordered = dateOrders.ordered || 0;
                    const dateSubscribed = dateSubscribedByType[mealType] || 0;
                    const mealPrice = MEAL_PRICES[mealType] || 0;
                    const isFixedPrice = mealType === 'Lunch' || mealType === 'Dinner';

                    // Calculate how many are covered by pool for this date
                    const covered = coveredByPool[date][mealType] || 0;
                    const { own, borrowed } = poolSplit[date][mealType] || { own: covered, borrowed: 0 };
                    const extraOrdered = Math.max(0, ordered - covered);

                    // Calculate delta: ordered - subscribed (for display)
                    let delta = ordered - dateSubscribed;

                    // Special snack logic (using weekly totals for snack calculation, matching billing script)
                    if (mealType === 'Snack') {
                        const weeklySubscribed = weeklyMealsByType[mealType].subscribed || 0;
                        const weeklyOrdered = weeklyMealsByType[mealType].ordered || 0;
                        if (weeklySubscribed === 0 || weeklyOrdered <= weeklySubscribed / snacksPerDay) {
                            delta = ordered - weeklySubscribed / snacksPerDay;
                        } else if (weeklyOrdered > weeklySubscribed) {
                            delta = ordered - weeklySubscribed;
                        } else {
                            delta = 0;
                        }
                    }

                    // Calculate meal charge based on extra ordered (not covered by pool)
                    let mealCharge = borrowed * planRate(mealType);
                    if (extraOrdered > 0) {
                        if (isFixedPrice) {
                            // Use fixed meal price
                            mealCharge += extraOrdered * mealPrice;
                        } else {
                            // Use sorted prices from orders (take first extraOrdered prices)
                            const prices = [...(dateOrders.price || [])].sort((a, b) => a - b);
                            mealCharge += prices.slice(0, extraOrdered).reduce((sum, price) => sum + price, 0);
                        }
                    }

                    // Allocate pool coverage to orders (prioritize expensive items)
                    const orderDetails = orderDetailsByDate[date][mealType] || [];
                    // Sort by price descending to prioritize expensive items for subscription coverage
                    const sortedOrderDetails = [...orderDetails].sort((a, b) => {
                        const priceA = mealType === 'Lunch' || mealType === 'Dinner' ? mealPrice : a.price;
                        const priceB = mealType === 'Lunch' || mealType === 'Dinner' ? mealPrice : b.price;
                        return priceB - priceA;
                    });

                    // Premium proteins: charge comes straight from the weekly pool allocation done
                    // once in PASS 1.5 — no per-date ratio or estimate. This is the single source
                    // of truth: the date total (premiumProteinCharge) and the per-order breakdown
                    // (finalOrderDetails[].premiumProtein — what actually gets sent to Stripe) both
                    // read the exact same per-order numbers, so they can never disagree, and a
                    // change on one date can never shift a dollar amount onto an unrelated date.
                    const totalPremiumMealsOrdered = Object.values(dateOrders.premium || {}).reduce((sum, qty) => sum + qty, 0);

                    let premiumProteinCharge = 0;
                    const premiumProteins = { count: totalPremiumMealsOrdered, charge: 0 };
                    const finalOrderDetails = [];
                    const tiers = [
                        { left: own, isIncluded: true, unitPrice: () => 0 },
                        { left: borrowed, isIncluded: false, isPlanRate: true, unitPrice: () => planRate(mealType) },
                        { left: Infinity, isIncluded: false, unitPrice: (od) => (isFixedPrice ? mealPrice : od.price) },
                    ];

                    for (const orderDetail of sortedOrderDetails) {
                        // Calculate premium charge for this order
                        let premiumChargeForOrder = null;
                        if (orderDetail.premiumProtein) {
                            const key = `${date}::${orderDetail.recordId}`;
                            const charge = weeklyPremiumChargeByOrder[mealType]?.[key] ?? 0;
                            premiumChargeForOrder = {
                                ...orderDetail.premiumProtein,
                                charge
                            };
                            premiumProteinCharge += charge;
                            premiumProteins.charge += charge;
                        }

                        // Split the order across own-plan, plan-rate and full-price units
                        let qtyLeft = orderDetail.quantity;
                        for (const tier of tiers) {
                            const qty = Math.min(qtyLeft, tier.left);
                            if (qty <= 0) continue;
                            tier.left -= qty;
                            qtyLeft -= qty;
                            finalOrderDetails.push({
                                ...orderDetail,
                                quantity: qty,
                                isIncluded: tier.isIncluded,
                                isPlanRate: !!tier.isPlanRate,
                                price: tier.unitPrice(orderDetail),
                                premiumProtein: premiumChargeForOrder && {
                                    ...premiumChargeForOrder,
                                    charge: premiumChargeForOrder.charge * (qty / orderDetail.quantity)
                                }
                            });
                        }
                    }

                    const totalCharge = mealCharge + premiumProteinCharge;

                    dateMeals[mealType] = {
                        subscribed: dateSubscribed,
                        ordered,
                        delta: delta,
                        amount: totalCharge,
                        premiumProteins: premiumProteins,
                        orderDetails: finalOrderDetails // Include order details for display
                    };

                    if (totalCharge > 0) {
                        dateCharges += totalCharge;
                    }
                });

                (prepaidRefundsByDate[date] || []).forEach(refund => {
                    if (!dateMeals['Add On']) {
                        dateMeals['Add On'] = { subscribed: 0, ordered: 0, delta: 0, amount: 0, premiumProteins: { count: 0, charge: 0 }, orderDetails: [] };
                    }
                    const meal = dateMeals['Add On'];
                    meal.delta -= refund.quantity;
                    meal.amount -= refund.amount;
                    meal.prepaidRefunds = [...(meal.prepaidRefunds || []), refund];
                    dateRefunds += refund.amount;
                });

                MEAL_TYPES.forEach(mealType => {
                    const unusedPool = poolSplit[date][mealType]?.refund || 0;
                    if (unusedPool <= 0) return;

                    const isPremiumPlan = mealType !== 'Snack' && (weeklyMealsByType[mealType]?.premiumSubscriptions || 0) > 0;
                    const perMealRefund = (MEAL_PRICES[mealType] || 0) + (isPremiumPlan ? PREMIUM_SURCHARGE : 0);
                    const refundAmount = unusedPool * perMealRefund * SUBSCRIPTION_DISCOUNT;
                    dateRefunds += refundAmount;

                    if (!dateMeals[mealType]) {
                        dateMeals[mealType] = {
                            subscribed: 0,
                            ordered: 0,
                            delta: -unusedPool,
                            amount: -refundAmount,
                            premiumProteins: { count: 0, charge: 0 }
                        };
                    } else {
                        dateMeals[mealType].delta -= unusedPool;
                        dateMeals[mealType].amount -= refundAmount;
                    }
                });

                const totalDateSubscribed = Object.values(dateMeals).reduce((sum, m) => sum + (m.subscribed || 0), 0);
                const totalDateOrdered = Object.values(dateMeals).reduce((sum, m) => sum + (m.ordered || 0), 0);

                dates[date] = {
                    weekKey,
                    isLocked,
                    isSkipped: isDateSkipped,
                    skipsOnSave: subs.some(sub => sub.nextDeliveryDate === date) && skipsOnSave(date, ordersByDeliveryDate, pendingChanges),
                    totalSubscribed: totalDateSubscribed,
                    totalOrdered: totalDateOrdered,
                    hasChanges: dateCharges > 0 || dateRefunds > 0,
                    charges: dateCharges,
                    refunds: dateRefunds,
                    net: dateCharges - dateRefunds,
                    meals: dateMeals
                };

                if (dateCharges > 0 || dateRefunds > 0) {
                    totalCharges += dateCharges;
                    totalRefunds += dateRefunds;
                    if (!datesWithChanges.includes(date)) {
                        datesWithChanges.push(date);
                    }
                }
            });

            // ===== Delivery Fee Calculation (per week) =====
            // deliveryFees is raw map from Airtable: "zipCode | day | time" → cost
            const deliveryFees = customerDetails.deliveryFees || {}; // keys already lowercase from backend

            if (Object.keys(deliveryFees).length > 0) {
                // Build subscription lookup by day name
                const subByDay = {};
                subs.forEach(sub => {
                    if (sub.deliveryDay) {
                        subByDay[sub.deliveryDay.toLowerCase()] = sub;
                    }
                });

                // Per-date delta = orderedFee(at this date's effective slot) - paidFee(from subscription on that day-of-week)
                const subDaysCovered = new Set();

                datesInWeek.forEach(date => {
                    const ordersForDate = ordersByDeliveryDate[date] || [];
                    const hasOrders = ordersForDate.some(o => o.quantity > 0);
                    if (!hasOrders) return;

                    const dayName = getDayOfWeek(date);
                    const dayLower = dayName.toLowerCase();
                    const matchingSub = subByDay[dayLower];
                    // Quote the fee for the slot we actually save
                    const { current: orderedTime, zipCode } = resolveDeliveryTime({
                        deliveryFees,
                        date,
                        ordersForDate,
                        subscriptions: subs,
                        deliveryTimeOverrides
                    });
                    const orderedKey = `${zipCode} | ${dayName} | ${orderedTime}`.toLowerCase();
                    const orderedFee = deliveryFees[orderedKey] ?? 9.99;

                    let paidFee = 0;
                    if (matchingSub && coversDate(matchingSub, date)) {
                        const paidKey = `${matchingSub.zipCode || ''} | ${matchingSub.deliveryDay} | ${matchingSub.deliveryTime || ''}`.toLowerCase();
                        paidFee = deliveryFees[paidKey] || 0;
                        subDaysCovered.add(dayLower);
                    }

                    const delta = orderedFee - paidFee;
                    if (delta !== 0 && dates[date]) {
                        dates[date].deliveryFee = (dates[date].deliveryFee || 0) + delta;
                        dates[date].hasChanges = true;
                        totalCharges += delta;
                        totalDeliveryFee += delta;
                        if (!datesWithChanges.includes(date)) datesWithChanges.push(date);
                    }
                });

                // Subscription days in this week with no orders → refund the paid fee once per unique slot
                const refundedKeys = new Set();
                subs.forEach(sub => {
                    if (!sub.deliveryDay) return;
                    const dayLower = sub.deliveryDay.toLowerCase();
                    if (subDaysCovered.has(dayLower)) return;

                    // A skipped date's fee was paid with the week, so it's refunded like any plan date with no meals.
                    const matchingDate = datesInWeek.find(date => getDayOfWeek(date).toLowerCase() === dayLower);
                    if (!matchingDate || !dates[matchingDate] || !coversDate(sub, matchingDate)) return;

                    const paidKey = `${sub.zipCode || ''} | ${sub.deliveryDay} | ${sub.deliveryTime || ''}`.toLowerCase();
                    if (refundedKeys.has(paidKey)) return;
                    refundedKeys.add(paidKey);

                    const paidFee = deliveryFees[paidKey] || 0;
                    if (paidFee === 0) return;

                    dates[matchingDate].deliveryFee = (dates[matchingDate].deliveryFee || 0) - paidFee;
                    dates[matchingDate].hasChanges = true;
                    totalCharges -= paidFee;
                    totalDeliveryFee -= paidFee;
                    if (!datesWithChanges.includes(matchingDate)) datesWithChanges.push(matchingDate);
                });
            }

            // After processing all dates in the week, save premium usage data
            premiumUsageByWeek[weekKey] = {};
            //debug
            //console.log('weeklyMealsByType', weeklyMealsByType);
            MEAL_TYPES.forEach(mealType => {
                if (mealType !== 'Add On') {
                    const weeklyPremiumSubscriptions = weeklyMealsByType[mealType].premiumSubscriptions || 0;
                    const totalWeeklyPremiumOrdered = weeklyMealsByType[mealType].premium
                        ? Object.values(weeklyMealsByType[mealType].premium).reduce((sum, qty) => sum + qty, 0)
                        : 0;

                    const hasExceeded = totalWeeklyPremiumOrdered >= weeklyPremiumSubscriptions;

                    premiumUsageByWeek[weekKey][mealType] = {
                        subscribed: weeklyPremiumSubscriptions,
                        ordered: totalWeeklyPremiumOrdered,
                        // Use >= instead of > because when all slots are used (ordered >= subscribed),
                        // the NEXT premium selection should be charged
                        hasExceeded: hasExceeded
                    };

                }
            });
        });

        return {
            summary: {
                totalCharges,
                totalRefunds,
                totalDeliveryFee,
                net: totalCharges - totalRefunds,
                netChange: totalCharges - totalRefunds, // Alias for compatibility
                netCharge: totalCharges - totalRefunds,
                datesWithChanges,
                customerName: customerDetails?.customer?.name
            },
            dates,
            premiumUsageByWeek // Track premium subscriptions vs orders per week per meal type
        };
    }, [customerDetails, deliveryDates, ordersByDeliveryDate, deliveryStatusState, deliveryTimeOverrides, pendingChanges]);

    // API functions
    const fetchMealPlan = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);

            const [{ customerDetails, MealsByDate }, paymentHistoryResult] = await Promise.all([
                mealPlanService.fetchMealPlan(token),
                mealPlanService.getPaymentHistory(token).catch(() => ({ paymentsByDate: {} }))
            ]);
            const sortedDates = sortDeliveryDates(Object.keys(MealsByDate));
            const landingDate = sortedDates.find(date =>
                MealsByDate[date].reduce((sum, o) => sum + (o.quantity || 0), 0) > 0
            ) || sortedDates[0];

            const adjustedByDate = Object.fromEntries(
                Object.entries(MealsByDate).map(([date, dateOrders]) =>
                    [date, adjustAvailableDishes(dateOrders, customerDetails)])
            );

            setCustomerDetails(customerDetails);
            setDeliveryDates(sortedDates);
            setOrdersByDeliveryDate(adjustedByDate);
            setSelectedDeliveryDate(landingDate);
            setOrders(adjustedByDate[landingDate] || []);
            setPriorPaymentsByDate(paymentHistoryResult.paymentsByDate || {});
            setPaymentRecords(paymentHistoryResult.paymentRecords || {});
            // console.log('customerDetails', customerDetails);
            // console.log('MealsByDate', MealsByDate);
            // console.log('sortedDates', sortedDates);

        } catch (err) {
            if (err.response?.data?.error === 'bad_admin_key') {
                clearAdminKey();
                setAdminKeyState('');
                setAdminError('Wrong admin password.');
                return;
            }
            if (err.response?.data?.error === 'not_owner') {
                setNotOwner(true);
                return;
            }
            if (err.response?.data?.error === 'no_active_subscription') {
                window.location.replace('/menu');
                return;
            }
            const errorMsg = err.response?.data?.error || 'Failed to load meal plan. Please try again later.';
            setError(errorMsg);
            setTimeout(() => setError(null), 5000);
        } finally {
            setLoading(false);
        }
    }, [token]);

    // (Stripe cancel redirect is handled in the initial load useEffect above)

    // Detect price changes whenever orders or delivery status changes
    useEffect(() => {
        if (customerDetails && deliveryDates.length > 0) {
            const changes = calculateBillingChanges();
            //debug
            //console.log('billingChanges', changes);
            setBillingChanges(changes);
        }
    }, [ordersByDeliveryDate, customerDetails, deliveryDates, deliveryStatusState, calculateBillingChanges]);

    // Helper functions
    const getSubscriptionCount = (mealType = null) => {
        if (!customerDetails?.subscriptions) return 0;
        const subscription = customerDetails.subscriptions.find(sub =>
            (mealType ? sub.mealType?.toLowerCase() === mealType.toLowerCase() : true) &&
            sub.deliveryDay.toLowerCase() === getDayOfWeek(selectedDeliveryDate).toLowerCase()
        );
        return subscription?.mealsIncluded || 0;
    };

    // Update delivery status for all dates based on subscription data
    const updateDeliveryStatuses = () => {
        if (!customerDetails?.subscriptions || !deliveryDates || deliveryDates.length === 0) return;

        const newStatuses = {};

        deliveryDates.forEach(date => {
            // Parse date to get day of week
            const parsedDate = parseEstDate(date);
            const dayOfWeek = parsedDate.format("dddd");

            // Find subscriptions for this day of week
            const daySubscription = customerDetails.subscriptions.find(sub =>
                sub.deliveryDay?.toLowerCase() === dayOfWeek.toLowerCase()
            );
            if (!daySubscription) {
                newStatuses[date] = { isSkipped: false, hasSubscription: false };
            } else {
                newStatuses[date] = {
                    isSkipped: (daySubscription.skippedDates || []).includes(date) && !isResumedDate(date, ordersByDeliveryDate),
                    hasSubscription: true,
                    nextDeliveryDate: daySubscription.nextDeliveryDate
                };
            }
        });

        setDeliveryStatusState(newStatuses);
    };

    const getOrderedMealCount = (mealType, deliveryDate = null) => {
        const targetDate = deliveryDate || selectedDeliveryDate;
        const orderedCount = orders
            .filter(order =>
                order.meal === mealType &&
                order.deliveryDate === targetDate &&
                order.quantity > 0
            )
            .reduce((total, order) => total + order.quantity, 0);
        return orderedCount;
    };

    const getPriceText = (mealType, subscribed, ordered) => {
        const snacksPerDay = customerDetails.customer.snacksPerDay;
        let finalText = [];
        if (mealType === 'Snack') {
            finalText.push(snacksPerDay > 1 ? `Recommended: ${snacksPerDay} snacks per day` : 'Recommended: 1 snack per day');
        }
        if (MEAL_PRICES[mealType] && mealType !== 'Add On' && mealType !== 'Snack' && mealType !== 'Breakfast') {
            finalText.push(`$${MEAL_PRICES[mealType]} per additional meal`);
        }
        return finalText.length > 0 ? `(${finalText.join(', ')})` : '';
    };

    const updateOrdersState = (updatedOrders) => {
        const sorted = sortQuantityFirst(
            updatedOrders,
            undefined,
            (order) => Boolean(order.hasAllergenConflict),
        );
        setOrders(sorted);
        setOrdersByDeliveryDate(prev => ({
            ...prev,
            [selectedDeliveryDate]: sorted
        }));

        // Update modal index if modal is open
        if (selectedMeal && showModal) {
            const newIndex = sorted.findIndex(order =>
                order.recordId === selectedMeal.recordId &&
                order.dishId === selectedMeal.dishId &&
                order.meal === selectedMeal.meal
            );
            if (newIndex !== -1) {
                setSelectedMeal({ ...sorted[newIndex], actualIndex: newIndex });
            }
        }
    };

    // Helper function to optimize pending changes by filtering redundant operations
    // Groups changes by recordId and applies filtering logic similar to backend
    const optimizePendingChanges = useCallback((changes) => {
        if (!changes || changes.length === 0) return [];

        // Sort by timestamp
        const sortedChanges = [...changes].sort((a, b) => a.timestamp - b.timestamp);

        // Group changes by recordId. Date-scoped changes (no recordId — e.g. CHANGE_DELIVERY_TIME)
        // pass through unmodified so they're never accidentally dropped.
        const changesByRecordId = {};
        const dateScopedChanges = [];
        for (const change of sortedChanges) {
            const recordId = change.recordId || change.order?.recordId;
            if (!recordId) {
                dateScopedChanges.push(change);
                continue;
            }

            if (!changesByRecordId[recordId]) {
                changesByRecordId[recordId] = [];
            }
            changesByRecordId[recordId].push({ ...change, _recordId: recordId });
        }

        const filteredChanges = [];
        for (const recordId in changesByRecordId) {
            const groupChanges = changesByRecordId[recordId].sort((a, b) => a.timestamp - b.timestamp);

            // Find all REMOVE_DISH changes where recordId starts with temp-
            const removeIndices = [];
            groupChanges.forEach((change, index) => {
                if (change.type === 'REMOVE_DISH' && recordId.startsWith('temp-')) {
                    removeIndices.push(index);
                }
            });

            if (removeIndices.length > 0) {

                // ADD then REMOVE for temp- recordId cancels out
                // Keep only changes that come after the last REMOVE_DISH
                const lastRemoveIndex = removeIndices[removeIndices.length - 1];
                const changesAfterLastRemove = groupChanges.slice(lastRemoveIndex + 1);
                if (changesAfterLastRemove.length > 0) {
                    filteredChanges.push(...changesAfterLastRemove);
                }


            } else {
                // Check if last change is REMOVE_DISH with non-temp recordId
                const lastChange = groupChanges[groupChanges.length - 1];
                if (lastChange.type === 'REMOVE_DISH' && !recordId.startsWith('temp-')) {
                    // Keep only the last REMOVE_DISH change (discard all modifications before deletion)
                    filteredChanges.push(lastChange);
                } else {
                    // No special REMOVE_DISH conditions, include all changes
                    filteredChanges.push(...groupChanges);
                }
            }
        }

        // Sort filtered changes by timestamp again to maintain overall order
        return [...filteredChanges, ...dateScopedChanges].sort((a, b) => a.timestamp - b.timestamp);
    }, []);

    // Helper function to track pending changes for a delivery date
    // Optimizes changes by filtering redundant operations (e.g., ADD then REMOVE cancels out)
    const trackPendingChange = useCallback((deliveryDate, change) => {
        // Add unique changeId to track individual actions
        const changeWithId = {
            ...change,
            changeId: `change-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        };

        setPendingChanges(prev => {
            const dateChanges = prev[deliveryDate] || {
                isDirty: false,
                changes: [],
                monetaryChange: false
            };

            // Add the new change and optimize
            const allChanges = [...dateChanges.changes, changeWithId];
            const optimizedChanges = optimizePendingChanges(allChanges);

            return {
                ...prev,
                [deliveryDate]: {
                    isDirty: optimizedChanges.length > 0,
                    changes: optimizedChanges,
                    monetaryChange: optimizedChanges.some(c => c.monetaryChange)
                }
            };
        });
    }, [optimizePendingChanges]);

    // Helper function to clear pending changes for a delivery date
    const clearPendingChanges = useCallback((deliveryDate) => {
        setPendingChanges(prev => {
            const { [deliveryDate]: _, ...rest } = prev;
            return rest;
        });
    }, []);

    // Helper function to remove only successful changes from pending changes
    const removeSuccessfulChanges = useCallback((deliveryDate, successfulChangeIds) => {
        setPendingChanges(prev => {
            const dateChanges = prev[deliveryDate];
            if (!dateChanges) return prev;

            const remainingChanges = dateChanges.changes.filter(
                change => !successfulChangeIds.includes(change.changeId)
            );

            if (remainingChanges.length === 0) {
                // All changes succeeded, remove the date entirely
                const { [deliveryDate]: _, ...rest } = prev;
                return rest;
            }

            return {
                ...prev,
                [deliveryDate]: {
                    isDirty: remainingChanges.length > 0,
                    changes: remainingChanges,
                    monetaryChange: remainingChanges.some(c => c.monetaryChange)
                }
            };
        });
    }, []);

    // Handle delivery time change for a date — updates override and tracks pending change.
    // If the new time matches the persisted time (existing order or subscription), clears the override
    // and removes any pending CHANGE_DELIVERY_TIME so we don't show "unsaved" for a no-op revert.
    const handleDeliveryTimeChange = useCallback((deliveryDate, newTime) => {
        const ordersForDate = ordersByDeliveryDate[deliveryDate] || [];
        const dayName = new Date(deliveryDate + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'long' });
        const matchingSub = withResumedDates(customerDetails?.subscriptions, ordersByDeliveryDate).find(s =>
            (s.deliveryDay || '').toLowerCase() === dayName.toLowerCase() && !s.isSkipped
        );
        const existingOrderTime = ordersForDate.find(o => o.deliveryTime)?.deliveryTime;
        const persistedTime = existingOrderTime || matchingSub?.deliveryTime || '';
        // Moved-in dishes still need the date's time written, so it's never a no-op
        const hasIncomingMove = Object.values(pendingChanges).some(entry => entry.changes.some(isMoveInto(deliveryDate)));
        const isRevert = !hasIncomingMove && persistedTime && persistedTime.toLowerCase() === newTime.toLowerCase();

        setDeliveryTimeOverrides(prev => {
            if (isRevert) {
                const { [deliveryDate]: _, ...rest } = prev;
                return rest;
            }
            return { ...prev, [deliveryDate]: newTime };
        });

        setPendingChanges(prev => {
            prev = retimeMovesInto(prev, deliveryDate, newTime);
            const dateChanges = prev[deliveryDate] || { isDirty: false, changes: [], monetaryChange: false };
            const filtered = dateChanges.changes.filter(c => c.type !== 'CHANGE_DELIVERY_TIME');

            const updatedChanges = isRevert ? filtered : [
                ...filtered,
                {
                    type: 'CHANGE_DELIVERY_TIME',
                    deliveryTime: newTime,
                    deliveryDate,
                    monetaryChange: true,
                    changeId: `change-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                    timestamp: Date.now()
                }
            ];

            if (updatedChanges.length === 0) {
                const { [deliveryDate]: _, ...rest } = prev;
                return rest;
            }

            return {
                ...prev,
                [deliveryDate]: {
                    isDirty: updatedChanges.length > 0,
                    changes: updatedChanges,
                    monetaryChange: updatedChanges.some(c => c.monetaryChange)
                }
            };
        });
    }, [ordersByDeliveryDate, customerDetails, pendingChanges]);

    // Helper function to update orders with real Airtable recordIds after save
    const updateOrderRecordIds = useCallback((deliveryDate, results) => {
        if (!results || !Array.isArray(results)) return;

        // If a CHANGE_DELIVERY_TIME succeeded, propagate the new time onto the local orders
        const successfulTimeChange = results.find(r => r.type === 'CHANGE_DELIVERY_TIME' && r.success);
        if (successfulTimeChange && successfulTimeChange.deliveryTime) {
            const newTime = successfulTimeChange.deliveryTime;
            setOrdersByDeliveryDate(prev => {
                const ordersForDate = prev[deliveryDate] || [];
                return { ...prev, [deliveryDate]: ordersForDate.map(o => ({ ...o, deliveryTime: newTime })) };
            });
            setDeliveryTimeOverrides(prev => {
                const { [deliveryDate]: _, ...rest } = prev;
                return rest;
            });
        }

        // Find ADD_DISH results that have new recordIds
        const recordIdMappings = results
            .filter(r => r.type === 'ADD_DISH' && r.success && r.recordId && r.originalTempRecordId)
            .map(r => ({ oldId: r.originalTempRecordId, newId: r.recordId }));

        if (recordIdMappings.length === 0) return;

        //console.log('📝 Updating order recordIds:', recordIdMappings);

        // Update orders for the delivery date
        setOrdersByDeliveryDate(prev => {
            const ordersForDate = prev[deliveryDate] || [];
            const updatedOrders = ordersForDate.map(order => {
                const mapping = recordIdMappings.find(m => m.oldId === order.recordId);
                if (mapping) {
                    return { ...order, recordId: mapping.newId };
                }
                return order;
            });
            return { ...prev, [deliveryDate]: updatedOrders };
        });

        // Also update current orders if viewing the same date
        if (selectedDeliveryDate === deliveryDate) {
            setOrders(prev => prev.map(order => {
                const mapping = recordIdMappings.find(m => m.oldId === order.recordId);
                if (mapping) {
                    return { ...order, recordId: mapping.newId };
                }
                return order;
            }));
        }
    }, [selectedDeliveryDate]);

    // Reproduces what the initial load applied to this dish (same seed), so a
    // draft opens on what the card was showing.
    const markOriginalIngredientsActive = (order) => resolveAllergyAdjustment(order, {
        allergyGroupIds: customerDetails?.customer?.clientAllergyGroups?.[order.meal] || [],
        allergyLabels: customerDetails?.customer?.clientAllergyLabels || {},
        isPremiumCustomer: isPremiumSubscription(subscriptions, order),
    });

    const hasPlannedOrders = (date) => (ordersByDeliveryDate[date] || []).some(o => o.isOrdered && o.quantity > 0);
    const handleDatesSkipped = (dates) => {
        fetchMealPlan();
        setSuccessMessage(`Changes saved. ${dates.map(formatDeliveryDate).join(', ')} had no meals, so ${dates.length > 1 ? 'those deliveries are' : 'that delivery is'} now skipped.`);
        setTimeout(() => setSuccessMessage(''), 10000);
    };
    // Reloads after a skip save but keeps unsaved edits on dates the skip didn't touch.
    const refreshAfterSkips = async (skip, unskip) => {
        const skipped = new Set(skip);
        const reset = new Set([...skip, ...unskip]);
        // A pending move half-lands on a skipped date, so both of its ends go back to the saved plan.
        Object.values(pendingChanges).forEach(entry => entry.changes.forEach(c => {
            if (c.type === 'MOVE_DELIVERY_DATE' && (skipped.has(c.fromDate) || skipped.has(c.toDate))) {
                reset.add(c.fromDate);
                reset.add(c.toDate);
            }
        }));
        const keep = new Set(Object.keys(pendingChanges).filter(d => !reset.has(d)));
        reset.forEach(clearPendingChanges);
        setDeliveryTimeOverrides(prev => Object.fromEntries(Object.entries(prev).filter(([d]) => !reset.has(d))));
        try {
            const { customerDetails: details, MealsByDate } = await mealPlanService.fetchMealPlan(token);
            const merged = Object.fromEntries(Object.entries(MealsByDate).map(([date, dateOrders]) => [
                date,
                keep.has(date) && ordersByDeliveryDate[date] ? ordersByDeliveryDate[date] : adjustAvailableDishes(dateOrders, details),
            ]));
            const dates = sortDeliveryDates(Object.keys(MealsByDate));
            const stayOn = dates.includes(selectedDeliveryDate) ? selectedDeliveryDate : dates[0];
            setCustomerDetails(details);
            setDeliveryDates(dates);
            setOrdersByDeliveryDate(merged);
            setSelectedDeliveryDate(stayOn);
            setOrders(merged[stayOn] || []);
        } catch {
            setError('Your deliveries were updated, but the plan could not be refreshed. Please reload the page.');
        }
    };
    const handleSkipsSaved = ({ skip, unskip }) => {
        refreshAfterSkips(skip, unskip);
        const parts = [
            skip.length && `Skipped ${skip.map(formatDeliveryDate).join(', ')}`,
            unskip.length && `Resumed ${unskip.map(formatDeliveryDate).join(', ')}`,
        ].filter(Boolean);
        setSuccessMessage(`${parts.join('. ')}.`);
        setTimeout(() => setSuccessMessage(''), 10000);
    };
    const handleResumeDate = async (date) => {
        setResumingDate(date);
        try {
            await mealPlanService.updateSkips(token, { unskip: [date] });
            handleSkipsSaved({ skip: [], unskip: [date] });
        } catch (err) {
            setResumeError(err.response?.data?.error || 'Failed to resume this delivery.');
        } finally {
            setResumingDate(null);
        }
    };
    const upcomingSkips = [...new Set((customerDetails?.subscriptions || []).flatMap(s => s.skippedDates || []))].sort();
    const planZipCode = () => (ordersByDeliveryDate[selectedDeliveryDate] || []).find(o => o.isOrdered && o.zipCode)?.zipCode
        || (customerDetails?.subscriptions || []).find(s => s.zipCode)?.zipCode || '';
    const resolveTimeFor = (date) => resolveDeliveryTime({
        deliveryFees: customerDetails?.deliveryFees || {},
        date,
        ordersForDate: ordersByDeliveryDate[date] || [],
        subscriptions: withResumedDates(customerDetails?.subscriptions, ordersByDeliveryDate),
        deliveryTimeOverrides,
        zipHint: planZipCode()
    }).current;


    // A pending move-out sweeps every Airtable record on its date, so it can't also receive one
    const moveTargets = showMoveModal ? getMoveTargets({
        fromDate: selectedDeliveryDate,
        deliveryDates,
        deliveryStatusState,
        deliveryFees: customerDetails?.deliveryFees || {},
        zipCode: planZipCode()
    }).filter(t => t.date === selectedDeliveryDate || !pendingChanges[t.date]?.changes?.some(c => c.type === 'MOVE_DELIVERY_DATE')) : [];

    const getDefaultMoveTime = (date, times) => {
        const match = (time) => times.find(t => t.toLowerCase() === (time || '').toLowerCase());
        return (hasPlannedOrders(date) && match(resolveTimeFor(date))) || match(resolveTimeFor(selectedDeliveryDate)) || times[0];
    };

    const moveSlotFee = (date, time) =>
        (customerDetails?.deliveryFees || {})[`${planZipCode()} | ${getDayOfWeek(date)} | ${time}`.toLowerCase()] || 0;
    const getMoveFeeDelta = (toDate, deliveryTime) =>
        moveSlotFee(toDate, deliveryTime) - moveSlotFee(selectedDeliveryDate, resolveTimeFor(selectedDeliveryDate));

    // A closed tab past its cutoff can still be reopened, but only at the time it already has
    const getAddSlots = (dates) => dates.flatMap(date => {
        const times = isDateLocked(date)
            ? [resolveTimeFor(date)]
            : getDeliveryTimeOptions(customerDetails?.deliveryFees || {}, getDayOfWeek(date).toLowerCase(), planZipCode());
        return times.map(time => ({ date, time, fee: moveSlotFee(date, time) }));
    });

    // An empty date keeps the pick as an override, so meals added there are stamped with it
    const handleAddDelivery = (date, time) => {
        if (isDateLocked(date)) return;
        if (!hasPlannedOrders(date)) {
            setDeliveryTimeOverrides(prev => ({ ...prev, [date]: time }));
        } else if (time.toLowerCase() !== (resolveTimeFor(date) || '').toLowerCase()) {
            handleDeliveryTimeChange(date, time);
        }
    };

    // Target also gets CHANGE_DELIVERY_TIME: bills its week too and puts every dish there on one slot
    const handleMoveDelivery = (toDate, deliveryTime) => {
        const fromDate = selectedDeliveryDate;
        setShowMoveModal(false);
        if (toDate === fromDate) {
            handleDeliveryTimeChange(fromDate, deliveryTime);
            return;
        }
        const fromOrders = ordersByDeliveryDate[fromDate] || [];
        const { nextFrom, nextTo, movedCount } = moveOrdersBetweenDates({
            fromOrders,
            toOrders: ordersByDeliveryDate[toDate] || [],
            toDate,
            deliveryTime
        });
        if (!movedCount) return;

        const movingIds = new Set(fromOrders.filter(o => o.isOrdered && o.quantity > 0).map(o => o.recordId));
        // Unsaved adds are written straight to toDate; only saved records need the backend move
        const movesSavedOrders = [...movingIds].some(id => !String(id).startsWith('temp-'));
        const followsMove = (c) => movingIds.has(c.recordId || c.order?.recordId);

        const adjustedFrom = adjustAvailableDishes(nextFrom, customerDetails);
        const adjustedTo = adjustAvailableDishes(nextTo, customerDetails);
        setOrdersByDeliveryDate(prev => ({ ...prev, [fromDate]: adjustedFrom, [toDate]: adjustedTo }));
        setDeliveryTimeOverrides(prev => {
            const { [fromDate]: _from, [toDate]: _to, ...rest } = prev;
            return rest;
        });

        setPendingChanges(prev => {
            // A move still pending into fromDate now lands on toDate instead
            const retargeted = Object.fromEntries(Object.entries(prev).map(([date, entry]) => [date, {
                ...entry,
                changes: entry.changes.map(c => (isMoveInto(fromDate)(c) ? { ...c, toDate } : c))
            }]));
            const next = retimeMovesInto(retargeted, toDate, deliveryTime);
            const now = Date.now();
            const fromChanges = next[fromDate]?.changes || [];
            const toChanges = [
                ...[...(next[toDate]?.changes || []), ...fromChanges.filter(followsMove)]
                    .filter(c => c.type !== 'CHANGE_DELIVERY_TIME')
                    .map(c => (c.type === 'ADD_DISH' ? { ...c, order: { ...c.order, deliveryDate: toDate, deliveryTime } } : c)),
                { type: 'CHANGE_DELIVERY_TIME', deliveryTime, deliveryDate: toDate, monetaryChange: true, changeId: newChangeId(), timestamp: now }
            ];
            const stayingChanges = [
                ...fromChanges.filter(c => !followsMove(c) && c.type !== 'CHANGE_DELIVERY_TIME'),
                ...(movesSavedOrders ? [{
                    type: 'MOVE_DELIVERY_DATE', fromDate, toDate, deliveryTime, itemCount: movedCount,
                    monetaryChange: true, changeId: newChangeId(), timestamp: now
                }] : [])
            ];
            const { [fromDate]: _from, ...rest } = next;
            return {
                ...rest,
                ...(stayingChanges.length > 0 && {
                    [fromDate]: { isDirty: true, monetaryChange: stayingChanges.some(c => c.monetaryChange), changes: stayingChanges }
                }),
                [toDate]: { isDirty: true, monetaryChange: true, changes: toChanges }
            };
        });

        setSelectedDeliveryDate(toDate);
        setOrders(adjustedTo);
        setReopenTabRequest({ date: toDate });
        setSuccessMessage(`Moved ${movedCount} item${movedCount === 1 ? '' : 's'} to ${formatDeliveryDate(toDate)} (${deliveryTime}). Save your changes to confirm.`);
        setTimeout(() => setSuccessMessage(''), 5000);
    };

    // Event handlers
    // Same rule as the "No meals yet" tab label, limited to dates that can still be edited.
    const statusesReady = deliveryDates.length > 0 && deliveryDates.every(d => deliveryStatusState[d]);
    const noMealsKey = statusesReady
        ? deliveryDates.filter(d => deliveryStatusState[d].hasSubscription && !deliveryStatusState[d].isSkipped
            && !hasPlannedOrders(d) && Date.now() < getCutoffTime(d).getTime()).join(',')
        : null;
    // Only a date that turns empty after load gets the notice, once per date per visit.
    useEffect(() => {
        if (noMealsKey === null) return;
        const current = noMealsKey ? noMealsKey.split(',') : [];
        const baseline = noMealsBaselineRef.current;
        noMealsBaselineRef.current = current;
        if (!baseline) return;
        const fresh = current.filter(d => !baseline.includes(d) && !noMealsNoticeShownRef.current.has(d));
        if (!fresh.length) return;
        fresh.forEach(d => noMealsNoticeShownRef.current.add(d));
        setNoMealsNotice(fresh);
    }, [noMealsKey]);

    const handleDeliveryDateChange = (deliveryDate) => {
        setSelectedDeliveryDate(deliveryDate);
        const ordersForDate = ordersByDeliveryDate[deliveryDate] || [];
        setOrders(ordersForDate);
    };

    const openModal = (meal, actualIndex) => {
        // An available dish opens as a draft to configure — it only joins the
        // plan once the customer confirms with "Add to Cart". Past the cutoff
        // (or on a skipped date) nothing can be added, so it stays the read-only
        // preview instead of a draft that can't be confirmed.
        const addsDisabled = isLocked || deliveryStatusState[selectedDeliveryDate]?.isSkipped;
        if (isAvailableDish(meal) && !addsDisabled) {
            openDraftFromOrder(actualIndex);
            return;
        }
        setSelectedMeal({ ...meal, actualIndex });
        setShowModal(true);
    };

    /** Opens the modal on an uncommitted copy of `orders[index]` — either the
     *  first serving of an available dish, or another serving of one that's
     *  already in the plan. `orders`, the billing summary and `pendingChanges`
     *  are all left untouched until commitDraft runs. */
    const openDraftFromOrder = (index) => {
        const order = orders[index];
        if (!order) return;
        const draft = isAvailableDish(order)
            // Joining the plan for the first time: original ingredients become
            // the active selection, minus allergens.
            ? {
                ...order,
                ...markOriginalIngredientsActive(order),
                quantity: 1,
                isOrdered: true,
                hasUnsavedChange: true,
            }
            // Another serving of a dish already in the plan: its own record, and
            // not a fresh allergy substitution event — drop the original's
            // autoSubstitutions record, otherwise the "swapped in" badge would
            // reappear on the new serving with stale info.
            : {
                ...order,
                recordId: `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
                quantity: 1,
                hasUnsavedChange: true,
                autoSubstitutions: [],
            };
        setSelectedMeal(null);
        setDraftMeal(draft);
        setShowModal(true);
    };

    /** "Add to Cart" — the draft, with everything the customer configured in the
     *  modal, becomes a real order plus the one ADD_DISH change that saves it.
     *  The backend builds the Airtable record straight from this order's
     *  ingredients (see processBatchChanges' ADD_DISH case), so the
     *  customizations travel with the add rather than as follow-up changes. */
    const commitDraft = () => {
        if (!draftMeal) return;

        const existingIndex = orders.findIndex(o => o.recordId === draftMeal.recordId);
        const updatedOrders = [...orders];
        if (existingIndex !== -1) updatedOrders[existingIndex] = draftMeal;
        else updatedOrders.push(draftMeal);

        // Stamp the new order with the slot the customer sees and is charged for.
        // draftMeal.deliveryTime is deliberately ignored: a second serving carries the
        // source order's persisted time, which a dropdown pick must be able to override.
        // Passing `updatedOrders` keeps the date from reading as an empty subscribed day.
        const { current: effectiveDeliveryTime, subscriptionDeliveryTime: subDeliveryTime } = resolveDeliveryTime({
            deliveryFees: customerDetails?.deliveryFees || {},
            date: selectedDeliveryDate,
            ordersForDate: updatedOrders,
            subscriptions: withResumedDates(customerDetails?.subscriptions, { ...ordersByDeliveryDate, [selectedDeliveryDate]: updatedOrders }),
            deliveryTimeOverrides,
            zipHint: draftMeal.zipCode
        });

        trackPendingChange(selectedDeliveryDate, {
            type: 'ADD_DISH',
            order: { ...draftMeal, deliveryTime: effectiveDeliveryTime, subscriptionDeliveryTime: subDeliveryTime },
            timestamp: Date.now(),
            monetaryChange: true
        });

        flushSync(() => updateOrdersState(updatedOrders));

        setSuccessMessage(`${draftMeal.itemName} added — remember to save your changes.`);
        setTimeout(() => setSuccessMessage(''), 4000);

        closeModal();
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedMeal(null);
        setDraftMeal(null);
    };

    const handleQuantityChange = (index, newQuantity) => {
        const order = orders[index];
        const oldQuantity = order.quantity;
        const finalQuantity = Math.max(0, parseInt(newQuantity) || 0);
        const scrollTop = modalRef.current?.scrollTop || 0;

        if (finalQuantity === oldQuantity) return; // No change

        if (finalQuantity > oldQuantity) {
            // Adding never writes straight into the plan: the dish opens in the
            // modal as a draft to configure, and commitDraft ("Add to Cart") is
            // what puts it in `orders` and `pendingChanges`.
            openDraftFromOrder(index);
            return;
        } else {
            let updatedOrders = [...orders];
            // Removing a meal - local state update only
            const hasSameDish = updatedOrders.some((obj, i) =>
                i !== index &&
                obj.meal === order.meal &&
                obj.dishId === order.dishId &&
                obj.deliveryDate === order.deliveryDate &&
                obj.itemName === order.itemName
            );

            // Track the change before modifying state
            trackPendingChange(selectedDeliveryDate, {
                type: 'REMOVE_DISH',
                recordId: order.recordId,
                dishName: order.itemName,
                timestamp: Date.now(),
                monetaryChange: true
            });

            if (!hasSameDish) {
                // Keep the dish in list but mark as not ordered
                updatedOrders[index] = {
                    ...updatedOrders[index],
                    quantity: 0,
                    isOrdered: false,
                    hasUnsavedChange: false
                };
            } else {
                // Remove the duplicate entry
                updatedOrders = updatedOrders.filter((_, i) => i !== index);
            }

            flushSync(() => updateOrdersState(updatedOrders));

            // setSuccessMessage(`Removed ${order.itemName}. Remember to "Save Changes" in Order Summary.`);
            // setTimeout(() => setSuccessMessage(''), 5000);
        }

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    /** The dish a modal edit targets: the uncommitted draft, or a live order. */
    const getEditTarget = (orderIndex) => (orderIndex === DRAFT_INDEX ? draftMeal : orders[orderIndex]);

    /** Writes a modal edit back. Draft edits stay local — the dish isn't in the
     *  plan yet, so there's nothing to bill or save, and the eventual ADD_DISH
     *  carries the finished configuration. Live orders update both the orders
     *  state (which is what the billing summary is derived from) and
     *  `pendingChanges`. */
    const applyOrderEdit = (orderIndex, updatedOrder, change) => {
        if (orderIndex === DRAFT_INDEX) {
            setDraftMeal(updatedOrder);
            return;
        }
        const updatedOrders = [...orders];
        updatedOrders[orderIndex] = updatedOrder;
        trackPendingChange(selectedDeliveryDate, change);
        flushSync(() => updateOrdersState(updatedOrders));
    };

    const handleProteinSubstitution = (orderIndex, newProteinId, oldProteinId) => {
        const order = getEditTarget(orderIndex);
        const newProteinOption = order.proteinOptions.find(opt => opt.id === newProteinId);
        const upgradePrice = newProteinOption?.priceSubscription || 0;

        // Only show modal if protein has an upgrade price
        if (upgradePrice > 0) {
            // Check if old protein was premium (if so, we're replacing, not adding)
            const oldProteinOption = order.proteinOptions?.find(opt => opt.id === oldProteinId);
            const oldProteinPrice = oldProteinOption?.priceSubscription || 0;
            const isReplacingPremium = oldProteinPrice > 0;

            // Use pre-calculated premium usage data from billingChanges
            if (!isReplacingPremium && billingChanges?.premiumUsageByWeek) {
                const mealType = order.meal;

                // Find which week this delivery date belongs to
                const datesByWeek = groupDatesByWeek(deliveryDates);
                let weekKey = null;
                for (const [key, datesInWeek] of Object.entries(datesByWeek)) {
                    if (datesInWeek.includes(selectedDeliveryDate)) {
                        weekKey = key;
                        break;
                    }
                }

                // Check if premium subscription limit has been exceeded
                const premiumUsage = billingChanges.premiumUsageByWeek[weekKey]?.[mealType];
                if (premiumUsage?.hasExceeded) {
                    // Customer has used up their premium subscription plan
                    setShowPriceConfirm({
                        orderIndex,
                        newProteinId,
                        oldProteinId,
                        proteinName: newProteinOption.variantName || newProteinOption.name,
                        price: upgradePrice
                    });
                    return;
                }
            }
        }

        executeProteinSubstitution(orderIndex, newProteinId, oldProteinId, 0);
    };

    const executeProteinSubstitution = (orderIndex, newProteinId, oldProteinId, upgradePrice = 0) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = getEditTarget(orderIndex);
        const newProteinOption = order.proteinOptions.find(opt => opt.id === newProteinId);
        const oldProteinOption = order.proteinOptions?.find(opt => opt.id === oldProteinId);

        const { updatedOptions, updatedFinalIngredients } = replaceSingleSelect(
            order.proteinOptions, order.finalIngredients, newProteinId, oldProteinId
        );

        const updatedOrder = {
            ...order,
            proteinOptions: updatedOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        applyOrderEdit(orderIndex, updatedOrder, {
            type: 'REPLACE_PROTEIN',
            recordId: order.recordId,
            dishName: order.itemName,
            timestamp: Date.now(),
            newProteinId,
            newProteinName: newProteinOption?.name || '',
            oldProteinId,
            oldProteinName: oldProteinOption?.name || '',
            upgradePrice,
            monetaryChange: upgradePrice > 0 ? true : false
        });

        setShowPriceConfirm(null);

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    const handleVeggieSubstitution = (orderIndex, targetVeggieId, isCurrentlyActive) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = getEditTarget(orderIndex);
        const targetVeggie = order.veggieOptions?.find(opt => opt.id === targetVeggieId);

        const { updatedOptions, updatedFinalIngredients } = toggleMultiSelect(
            order.veggieOptions, order.finalIngredients, targetVeggieId, isCurrentlyActive
        );

        const updatedOrder = {
            ...order,
            veggieOptions: updatedOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        applyOrderEdit(orderIndex, updatedOrder, {
            type: 'TOGGLE_VEGGIE',
            recordId: order.recordId,
            dishName: order.itemName,
            veggieId: targetVeggieId,
            veggieName: targetVeggie?.name,
            action: isCurrentlyActive ? 'remove' : 'add',
            timestamp: Date.now(),
            monetaryChange: false
        });

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    const handleSauceSubstitution = (orderIndex, newSauceId, oldSauceId) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = getEditTarget(orderIndex);
        const oldSauce = order.sauceOptions?.find(opt => opt.id === oldSauceId);
        const newSauce = order.sauceOptions?.find(opt => opt.id === newSauceId);

        const { updatedOptions, updatedFinalIngredients } = replaceSingleSelect(
            order.sauceOptions, order.finalIngredients, newSauceId, oldSauceId
        );

        const updatedOrder = {
            ...order,
            sauceOptions: updatedOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        applyOrderEdit(orderIndex, updatedOrder, {
            type: 'REPLACE_SAUCE',
            recordId: order.recordId,
            dishName: order.itemName,
            newSauceId,
            newSauceName: newSauce?.name || '',
            oldSauceId,
            oldSauceName: oldSauce?.name || '',
            timestamp: Date.now(),
            monetaryChange: false
        });

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    const handleStarchSubstitution = (orderIndex, newStarchId, oldStarchId) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = getEditTarget(orderIndex);
        const oldStarch = order.starchOptions?.find(opt => opt.id === oldStarchId);
        const newStarch = order.starchOptions?.find(opt => opt.id === newStarchId);

        const { updatedOptions, updatedFinalIngredients } = replaceSingleSelect(
            order.starchOptions, order.finalIngredients, newStarchId, oldStarchId
        );

        const updatedOrder = {
            ...order,
            starchOptions: updatedOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        applyOrderEdit(orderIndex, updatedOrder, {
            type: 'REPLACE_STARCH',
            recordId: order.recordId,
            dishName: order.itemName,
            timestamp: Date.now(),
            newStarchId,
            newStarchName: newStarch?.name || '',
            oldStarchId,
            oldStarchName: oldStarch?.name || '',
            monetaryChange: false
        });

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    const handleToggleGarnish = (orderIndex, garnishId, isCurrentlyActive) => {
        const scrollTop = modalRef.current?.scrollTop || 0;
        const order = getEditTarget(orderIndex);
        const { updatedOptions, updatedFinalIngredients } = toggleMultiSelect(
            order.garnishOptions, order.finalIngredients, garnishId, isCurrentlyActive
        );

        const updatedOrder = {
            ...order,
            garnishOptions: updatedOptions,
            finalIngredients: updatedFinalIngredients,
            hasUnsavedChange: true
        };

        applyOrderEdit(orderIndex, updatedOrder, {
            type: 'TOGGLE_GARNISH',
            recordId: order.recordId,
            dishName: order.itemName,
            garnishId,
            action: isCurrentlyActive ? 'remove' : 'add',
            monetaryChange: false
        });

        if (modalRef.current) modalRef.current.scrollTop = scrollTop;
    };

    if (showAdminPrompt) {
        const submitAdminKey = () => {
            const key = adminInput.trim();
            if (!key) return;
            setAdminKey(key);
            setAdminError('');
            setLoading(true);
            setAdminKeyState(key);
        };
        return (
            <GateCard
                title="Admin access"
                body="Enter the admin password to open this customer's meal plan."
                action="Open meal plan"
                onAction={submitAdminKey}
            >
                <input
                    type="password"
                    value={adminInput}
                    onChange={(e) => setAdminInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') submitAdminKey(); }}
                    placeholder="Admin password"
                    autoFocus
                    style={{
                        width: '100%', boxSizing: 'border-box', padding: '10px 12px', fontSize: '16px',
                        border: '1px solid #d8d0bd', borderRadius: '8px', marginTop: '8px',
                    }}
                />
                {adminError && <p style={{ color: '#c33', fontSize: '14px', marginTop: '8px' }}>{adminError}</p>}
            </GateCard>
        );
    }

    if (!adminKey && !authLoading && !isAuthenticated) {
        return (
            <GateCard
                title="Sign in to see your meal plan"
                body="Use the email address your Square Fare subscription is linked to."
                action={loginBusy ? 'Redirecting…' : 'Sign in'}
                onAction={handleLogin}
                disabled={loginBusy}
            />
        );
    }

    if (notOwner) {
        return (
            <GateCard
                title="This meal plan belongs to a different account"
                body={<>
                    You're signed in with an email that doesn't match this plan.
                    <br />
                    Please sign in with the correct account to manage your meals.
                </>}
                action="View This Week's Menu"
                onAction={() => {
                    setNotOwner(false);
                    setLoading(true);
                    setParams({}, { replace: true });
                }}
            />
        );
    }

    // Render loading state
    if (loading) {
        return (
            <CountdownLoader
                duration={30}
                onComplete={() => setLoading(false)}
            />
        );
    }

    // Render error state
    if (error) {
        return (
            <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '20px' }}>
                <div style={{
                    backgroundColor: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: '8px',
                    padding: '20px'
                }}>
                    <h2 style={{ color: '#c33', marginBottom: '10px' }}>Error</h2>
                    <p style={{ color: '#c33' }}>{error}</p>
                    <button
                        onClick={fetchMealPlan}
                        style={{
                            marginTop: '15px',
                            padding: '10px 20px',
                            backgroundColor: '#c33',
                            color: 'white',
                            border: 'none',
                            borderRadius: '5px',
                            cursor: 'pointer'
                        }}
                    >
                        Go Back to Meal Plan
                    </button>
                </div>
            </div>
        );
    }

    // Wait for data to load before rendering main content
    if (!customerDetails) {
        return null;
    }

    const customer = customerDetails.customer;
    const subscriptions = customerDetails.subscriptions;

    return (
        // Was rgba(255,252,248,255) — the fourth value is alpha, which is 0–1, not
        // 0–255. Browsers that don't clamp it drop the whole declaration and the
        // page falls back to white, which is why the gutters didn't match the
        // cream header. Use the shared token so it can't drift again.
        <div className="meal-plan-layout" style={{ backgroundColor: COLORS.cream }}>
            {/* Outside .meal-plan-content so the banner runs the full width of the
                window. Inside the 1000px column it stopped short on both sides,
                which is what made the page look boxed-in on wide screens. */}
            {adminKey && (
                <div style={{
                    background: '#fff8e6', borderBottom: '1px solid #f0dca6', color: '#7a5c00',
                    padding: '10px 16px', fontSize: '14px', textAlign: 'center', fontFamily: FONTS.sans,
                }}>
                    Admin view of {customer?.name || 'this customer'}&apos;s plan. Changes save to their account.{' '}
                    <button
                        type="button"
                        onClick={() => { clearAdminKey(); window.location.replace(`/meal-plan?customer=${token}`); }}
                        style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', fontWeight: 700, textDecoration: 'underline', cursor: 'pointer', color: 'inherit' }}
                    >
                        Exit admin
                    </button>
                </div>
            )}
            <MealPlanHeader customer={customer} />
            <div className="meal-plan-content">
                <SuccessMessage
                    message={successMessage}
                    onClose={() => setSuccessMessage('')}
                />
                <ErrorMessage
                    message={error}
                    onClose={() => setError(null)}
                />
                <ErrorMessage
                    message={resumeError}
                    onClose={() => setResumeError('')}
                />

                <DeliveryDateTabs
                    customerDetails={customerDetails}
                    deliveryDates={deliveryDates}
                    selectedDeliveryDate={selectedDeliveryDate}
                    ordersByDeliveryDate={ordersByDeliveryDate}
                    deliveryStatusState={deliveryStatusState}
                    onDeliveryDateChange={handleDeliveryDateChange}
                    panelBorderColor={panelBorder}
                    token={token}
                    canEdit={!isLocked && !deliveryStatusState[selectedDeliveryDate]?.isSkipped && hasPlannedOrders(selectedDeliveryDate)}
                    onEdit={() => setShowMoveModal(true)}
                    onResume={customerDetails?.subscriptions?.length > 0 ? handleResumeDate : undefined}
                    resumingDate={resumingDate}
                    upcomingSkips={upcomingSkips}
                    onManageSkips={customerDetails?.subscriptions?.length > 0 ? () => setShowSkipModal(true) : undefined}
                    reopenRequest={reopenTabRequest}
                    getAddSlots={getAddSlots}
                    onAddDelivery={handleAddDelivery}
                />

                <div style={{ borderLeft: `2px solid ${panelBorder}`, borderRight: `2px solid ${panelBorder}`, borderBottom: `2px solid ${panelBorder}`, borderRadius: '0 0 12px 12px', marginBottom: '56px', display: 'flow-root' }}>

                <LockMessage isLocked={isLocked} />
                {/* Meal Orders */}
                <div ref={containerRef} style={{ padding: '0px 20px 20px 20px', position: 'relative' }}>

                    {/* Group orders by meal type */}
                    {MEAL_TYPES.map(mealType => {
                        const mealOrders = orders.filter(order => order.meal === mealType && (!order.excludeFromSubscriptions || order.isOrdered));

                        if (mealOrders.length === 0) return null;

                        const categoryGroups = mealType === 'Add On' ? groupAddOnsByCategory(mealOrders) : [];
                        const rows = categoryGroups.some(g => g.id !== 'uncategorized')
                            ? categoryGroups.map(g => ({ key: `${mealType}-${g.id}`, label: g.label, orders: g.items }))
                            : [{ key: mealType, label: null, orders: mealOrders }];

                        const subscriptionCount = getSubscriptionCount(mealType);
                        const orderedCount = getOrderedMealCount(mealType, selectedDeliveryDate);


                        return (
                            <div key={mealType} style={{ marginBottom: '30px' }}>
                                <h4 style={{
                                    color: COLORS.dark,
                                    fontFamily: FONTS.serif,
                                    fontSize: '26px',
                                    fontWeight: 700,
                                    marginBottom: '15px',
                                    paddingBottom: '8px',
                                    borderBottom: `2px solid ${COLORS.creamAlt}`
                                }}>
                                    {{ Breakfast: 'Tailored Breakfast', Lunch: 'Tailored Lunch', Dinner: 'Tailored Dinner', Snack: 'Tailored Snacks' }[mealType] || mealType}
                                    <span style={{
                                        fontSize: '14px',
                                        color: '#666',
                                        fontWeight: 'normal',
                                        marginLeft: '8px'
                                    }}>
                                        {getPriceText(mealType, subscriptionCount, orderedCount)}
                                    </span>
                                </h4>

                                {rows.map(row => (
                                    <Fragment key={row.key}>
                                        {row.label && (
                                            <h5 style={{ color: COLORS.dark, fontFamily: FONTS.serif, fontSize: '18px', fontWeight: 600, margin: '18px 0 10px' }}>
                                                {row.label}
                                            </h5>
                                        )}
                                        <div style={{ position: 'relative' }}>
                                            {/* Scrolling container */}
                                            <div
                                                data-meal-type={row.key}
                                                onScroll={(e) => handleScroll(row.key, e)}
                                                style={{
                                                    display: 'flex',
                                                    overflowX: 'auto',
                                                    gap: '15px',
                                                    paddingBottom: '10px'
                                                }}
                                            >
                                                {row.orders.map((order) => {
                                                    const actualIndex = orders.indexOf(order);
                                                    return (
                                                        <CompactMealCard
                                                            key={order.recordId || actualIndex}
                                                            meal={order}
                                                            actualIndex={actualIndex}
                                                            onOpenModal={openModal}
                                                            onQuantityChange={handleQuantityChange}
                                                            isLocked={isLocked || deliveryStatusState[selectedDeliveryDate]?.isSkipped}
                                                            saving={saving}
                                                            showPrice={order.meal !== 'Lunch' && order.meal !== 'Dinner'}
                                                            addLabel={isAvailableDish(order) ? 'Customize' : 'Add Another'}
                                                        />
                                                    );
                                                })}
                                            </div>

                                            <ScrollArrows
                                                mealType={row.key}
                                                shouldShowArrows={shouldShowArrows}
                                                itemCount={row.orders.length}
                                                scrollState={getScrollState(row.key)}
                                                onScrollLeft={scrollLeft}
                                                onScrollRight={scrollRight}
                                            />
                                        </div>
                                    </Fragment>
                                ))}
                            </div>
                        );
                    })}
                </div>
                </div>

                {/* Modal Components */}
                {showModal && (selectedMeal || draftMeal) && (() => {
                    // A draft isn't in `orders`, so it reports DRAFT_INDEX as its
                    // actualIndex and every handler routes on that; a live order
                    // is re-read from `orders` so edits show immediately.
                    const modalMeal = draftMeal
                        ? { ...draftMeal, actualIndex: DRAFT_INDEX }
                        : selectedMeal.actualIndex !== undefined
                            ? { ...orders[selectedMeal.actualIndex], actualIndex: selectedMeal.actualIndex }
                            : selectedMeal;
                    return (
                        <MealModal
                            meal={modalMeal}
                            onClose={closeModal}
                            modalRef={modalRef}
                            saving={saving}
                            // Pass all the substitution handlers as props to the modal
                            onProteinSubstitution={handleProteinSubstitution}
                            onVeggieSubstitution={handleVeggieSubstitution}
                            onSauceSubstitution={handleSauceSubstitution}
                            onStarchSubstitution={handleStarchSubstitution}
                            onToggleGarnish={handleToggleGarnish}
                            isLocked={isLocked || deliveryStatusState[selectedDeliveryDate]?.isSkipped}
                            // A draft gets explicit Cancel / Add to Cart actions — it's
                            // the only way it enters the plan. An order already in the
                            // plan keeps the existing edit-as-you-click + × behaviour.
                            isDraft={Boolean(draftMeal)}
                            onAddToCart={draftMeal ? commitDraft : undefined}
                            addToCartLabel="Add to Cart"
                            // Auto-substitution badges belong to the pass where the dish
                            // is first being configured, i.e. the draft.
                            showAddedBanner={Boolean(draftMeal)}
                            // Pass billing changes which includes premium usage data
                            billingChanges={billingChanges}
                            deliveryDates={deliveryDates}
                            selectedDeliveryDate={selectedDeliveryDate}
                            showPrice={modalMeal.meal !== 'Lunch' && modalMeal.meal !== 'Dinner'}
                        />
                    );
                })()}

                {showPriceConfirm && (
                    <PremiumProteinUpgradeModal
                        confirmData={showPriceConfirm}
                        onConfirm={() => {
                            executeProteinSubstitution(
                                showPriceConfirm.orderIndex,
                                showPriceConfirm.newProteinId,
                                showPriceConfirm.oldProteinId,
                                showPriceConfirm.price
                            );
                        }}
                        onCancel={() => setShowPriceConfirm(null)}
                    />
                )}

                {showMoveModal && (
                    <MoveDeliveryModal
                        fromDate={selectedDeliveryDate}
                        itemCount={(ordersByDeliveryDate[selectedDeliveryDate] || []).filter(o => o.isOrdered && o.quantity > 0).length}
                        targets={moveTargets}
                        getDefaultTime={getDefaultMoveTime}
                        getFeeDelta={getMoveFeeDelta}
                        hasOrdersOn={hasPlannedOrders}
                        onConfirm={handleMoveDelivery}
                        onCancel={() => setShowMoveModal(false)}
                    />
                )}

                {noMealsNotice.length > 0 && (
                    <NoMealsNoticeModal
                        dates={noMealsNotice}
                        onClose={() => setNoMealsNotice([])}
                    />
                )}

                {showSkipModal && (
                    <SkipDeliveriesModal
                        customerToken={token}
                        currentWeekDates={deliveryDates}
                        datesWithOrders={deliveryDates.filter(hasPlannedOrders)}
                        onClose={() => setShowSkipModal(false)}
                        onSaved={(changes) => {
                            setShowSkipModal(false);
                            handleSkipsSaved(changes);
                        }}
                    />
                )}

                {/* Summary Modal - only for tablet & mobile */}
                {showSummaryModal && !isDesktop && (
                    <SummaryModal
                        ordersByDeliveryDate={ordersByDeliveryDate}
                        deliveryDates={deliveryDates}
                        billingChanges={billingChanges}
                        customerToken={token}
                        pendingChanges={pendingChanges}
                        deliveryTimeOverrides={deliveryTimeOverrides}
                        priorPaymentsByDate={priorPaymentsByDate}
                        paymentRecords={paymentRecords}
                        onSaveSuccess={(date, results, allSucceeded) => {
                            updateOrderRecordIds(date, results);
                            if (allSucceeded) {
                                clearPendingChanges(date);
                            } else {
                                const successfulChangeIds = results
                                    .filter(r => r.success && r.changeId)
                                    .map(r => r.changeId);
                                removeSuccessfulChanges(date, successfulChangeIds);
                            }
                        }}
                        onClose={() => setShowSummaryModal(false)}
                        onDatesSkipped={handleDatesSkipped}
                        customerDetails={customerDetails}
                    />
                )}
            </div>

            {/* Desktop: cart-style drawer (backdrop + closable sidebar) */}
            {isDesktop && summaryOpen && (
                <div
                    onClick={() => setSummaryOpen(false)}
                    style={{
                        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.30)', zIndex: 110,
                    }}
                    aria-hidden="true"
                />
            )}
            {isDesktop && summaryOpen && (
                <OrderSummarySidebar
                    onClose={() => setSummaryOpen(false)}
                    ordersByDeliveryDate={ordersByDeliveryDate}
                    deliveryDates={deliveryDates}
                    billingChanges={billingChanges}
                    customerToken={token}
                    pendingChanges={pendingChanges}
                    deliveryTimeOverrides={deliveryTimeOverrides}
                    priorPaymentsByDate={priorPaymentsByDate}
                    paymentRecords={paymentRecords}
                    onSaveSuccess={(date, results, allSucceeded) => {
                        updateOrderRecordIds(date, results);
                        if (allSucceeded) {
                            clearPendingChanges(date);
                        } else {
                            const successfulChangeIds = results
                                .filter(r => r.success && r.changeId)
                                .map(r => r.changeId);
                            removeSuccessfulChanges(date, successfulChangeIds);
                        }
                    }}
                    onDatesSkipped={handleDatesSkipped}
                    customerDetails={customerDetails}
                />
            )}

            {/* Tablet & Mobile: Floating button */}
            {!isDesktop && (
                <FloatingOrderButton onClick={() => setShowSummaryModal(true)} />
            )}

            {/* Desktop: floating cart button opens the summary drawer */}
            {isDesktop && !summaryOpen && (() => {
                const summaryCount = Object.values(ordersByDeliveryDate || {})
                    .flat()
                    .reduce((n, o) => n + (Number(o?.quantity) || 0), 0);
                return (
                    <button
                        type="button"
                        onClick={() => setSummaryOpen(true)}
                        className={hasUnsavedChanges ? 'order-summary-fab-unsaved' : undefined}
                        title={hasUnsavedChanges ? 'You have unsaved changes — open to save them' : undefined}
                        aria-label={`Open order summary, ${summaryCount} meals${hasUnsavedChanges ? ', you have unsaved changes' : ''}`}
                        style={{
                            position: 'fixed', right: '26px', bottom: '26px', zIndex: 100,
                            width: '60px', height: '60px', borderRadius: '50%',
                            background: '#ff7c44', border: 'none', cursor: 'pointer',
                            boxShadow: '0 6px 18px rgba(0,0,0,0.25)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}
                    >
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#fff"
                            strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M9 3h6a1 1 0 0 1 1 1v1a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
                            <path d="M8 4.5H6.5A1.5 1.5 0 0 0 5 6v13.5A1.5 1.5 0 0 0 6.5 21h11a1.5 1.5 0 0 0 1.5-1.5V6a1.5 1.5 0 0 0-1.5-1.5H16" />
                            <line x1="9" y1="11" x2="15" y2="11" />
                            <line x1="9" y1="14.5" x2="15" y2="14.5" />
                            <line x1="9" y1="18" x2="12.5" y2="18" />
                        </svg>
                        {summaryCount > 0 && (
                            <span style={{
                                position: 'absolute', top: '-4px', right: '-4px',
                                background: '#33343b', color: '#fff', borderRadius: '50%',
                                minWidth: '22px', height: '22px', fontSize: '12px', fontWeight: 700,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                padding: '0 4px',
                            }}>{summaryCount}</span>
                        )}
                        {/* Opposite corner from the meal count, so the two badges
                            never sit on top of each other. */}
                        {hasUnsavedChanges && (
                            <span
                                className="order-summary-unsaved-dot"
                                aria-hidden="true"
                                style={{
                                    position: 'absolute', top: '-5px', left: '-5px',
                                    background: '#d97706', color: '#fff',
                                    border: '2px solid #fff', borderRadius: '50%',
                                    width: '24px', height: '24px',
                                    fontSize: '14px', fontWeight: 700, lineHeight: 1,
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                }}
                            >!</span>
                        )}
                    </button>
                );
            })()}

            {/* Payment processing modal overlay */}
            {paymentProcessingModal && (
                <div style={{
                    position: 'fixed',
                    top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.6)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 3000
                }}>
                    <div style={{
                        backgroundColor: '#fff',
                        borderRadius: '12px',
                        padding: '32px 24px',
                        maxWidth: '420px',
                        width: '90%',
                        textAlign: 'center',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.25)'
                    }}>
                        {paymentProcessingModal === 'processing' && (
                            <>
                                <div style={{ fontSize: '36px', marginBottom: '16px' }}>&#8987;</div>
                                <h3 style={{ margin: '0 0 8px', color: '#333', fontSize: '20px' }}>Processing Payment</h3>
                                <p style={{ color: '#666', fontSize: '15px', margin: 0 }}>Applying your changes, please wait...</p>
                            </>
                        )}
                        {paymentProcessingModal === 'success' && (
                            <>
                                <div style={{ fontSize: '36px', marginBottom: '16px' }}>&#10003;</div>
                                <h3 style={{ margin: '0 0 8px', color: '#28a745', fontSize: '20px' }}>Payment Successful</h3>
                                <p style={{ color: '#666', fontSize: '15px', margin: '0 0 20px' }}>Your changes have been saved.</p>
                                <button
                                    onClick={() => setPaymentProcessingModal(null)}
                                    style={{
                                        padding: '10px 32px',
                                        backgroundColor: '#ff7c44',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >OK</button>
                            </>
                        )}
                        {paymentProcessingModal === 'failed' && (
                            <>
                                <div style={{ fontSize: '36px', marginBottom: '16px' }}>&#9888;</div>
                                <h3 style={{ margin: '0 0 8px', color: '#333', fontSize: '20px' }}>Payment Received</h3>
                                <p style={{ color: '#666', fontSize: '15px', margin: '0 0 20px' }}>We ran into an issue applying your changes — our team has been notified and will take care of it shortly. No action needed on your end.</p>
                                <button
                                    onClick={() => setPaymentProcessingModal(null)}
                                    style={{
                                        padding: '10px 32px',
                                        backgroundColor: '#ff7c44',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '16px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >OK</button>
                            </>
                        )}
                        {paymentProcessingModal === 'timeout' && (
                            <>
                                <div style={{ fontSize: '36px', marginBottom: '16px' }}>&#9888;</div>
                                <h3 style={{ margin: '0 0 8px', color: '#333', fontSize: '20px' }}>Payment Received</h3>
                                <p style={{ color: '#666', fontSize: '15px', margin: '0 0 20px' }}>Your changes are being processed. Please refresh in a few minutes if they don't appear. <br />Contact us at <a href="mailto:team@getsquarefare.com" style={{ color: '#ff7c44' }}>team@getsquarefare.com</a> if you need help.</p>
                                <button
                                    onClick={() => setPaymentProcessingModal(null)}
                                    style={{
                                        padding: '10px 32px',
                                        backgroundColor: '#ff7c44',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontSize: '1px',
                                        fontWeight: '600',
                                        cursor: 'pointer'
                                    }}
                                >OK</button>
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}